import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import {NgbActiveModal, NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {FormArray, FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {AppStateService} from "../../../core-module/services/app-state.service";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {Router} from "@angular/router";
import {saveAs} from "file-saver";
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { decodeReaderCapabilities } from '../../constants/reader-device.constants';

import {SelectingDeviceModalComponent} from "../selecting-device-modal/selecting-device-modal.component";
import {DatePipe} from "@angular/common";
import {
  FeatureFilePartialCompatibleModalComponent
} from "src/app/feature-file-partial-compatible-modal/feature-file-partial-compatible-modal.component";
import {ErrorComponent} from "src/app/error/error.component";
import {environment} from "../../../../environments/environment";
import { AloAocStateService } from "../../../core-module/services/state/alo-aoc-state.service";
import { AvailableFeaturesStateService } from "../../../core-module/services/state/available-features-state.service";
import { CloStateService } from "../../../core-module/services/state/clo-state.service";
import { DeviceConfigStateService } from "../../../core-module/services/state/device-config-state.service";
import { FeatureUtilsService } from "../../../core-module/services/state/feature-utils.service";
import { MultiOneHttpClient } from "../../../core-module/services/state/multi-one-http-client.service";
import { UiSignalsStateService } from "../../../core-module/services/state/ui-signals-state.service";
import { UserPreferencesStateService } from "../../../core-module/services/state/user-preferences-state.service";
import {
  CONFIGURATION_MODES,
  DEFAULT_CONFIGURATION_MODE,
  DEFAULT_DEVICE_REGIONS,
  DEVICE_SCHEMA_DETAILS_PATH,
  DEVICE_SEARCH_QUERY_PREFIX,
  DEVICE_SEARCH_ENDPOINT,
  DEVICE_STATUS_ENDPOINT,
  EASY_DESIGN_API_DOMAIN,
  LIGHT_CONTROL_FEATURE_VALUES,
  LUMINAIRE_FEATURE_VALUES,
  LUMINAIRE_INFO_FEATURE_VALUES,
  STANDARD_CONFIGURATION_MODE,
} from "src/app/shared-module/constants/configure-device-modal.constants";
import { matchesFeatureSelectionAlias } from "src/app/shared-module/constants/feature-supported.constants";
interface NamedDevice {
  name: string;
}

interface DeviceSearchItem {
  device12nc: string;
  deviceName: string;
  [key: string]: unknown;
}

const ERROR_MODAL_WINDOW_CLASS = "error-style, error-style1";
const INVALID_DATA_MESSAGE = "invaliddata.";
const MODAL_BACKDROP_STATIC = "static";

const CONFIGURE_DEVICE_FEATURE_LABELS: Readonly<Record<string, string>> = {
  alo: "ALO",
  clo: "CLO",
  aoc: "AOC",
  corridor: "Corridor",
  dynadimmer: "Dyna Dimmer",
  dimminginterface: "dimminginterface",
  mtp: "Mtp",
  ampdim: "Amp Dim",
  lineswitch: "Line Switch",
  owp: "Owp",
  codedmains: "Codedmains",
  dalipsu: "DALI PSU",
  daliaddressing102: "DALI 102 Addressing",
  daliconfiguration102and207: "DALI Configuration 102 and 207",
  aux: "Aux Output Voltage",
  lsi: "LSI",
  aocmultichannel: "AOC M-Ch",
  srpsu: "SR PSU",
  luminfo: "Luminaire info",
  ztvoutput: "ztvoutput",
  lip: "lip",
  rso: "rso",
  dtl: "DTL",
  flextune: "FlexTune",
  multiconfigmode: "Multi Config Mode",
  mindimlevel: "Min Dim Level",
  dcemergency: "DC Emergency",
  clolite: "CLO Lite",
  touchdim: "Touch and Dim",
  diia253maintenance: "diia253maintenance",
};

// DO NOT add `changeDetection: ChangeDetectionStrategy.OnPush` to this component.
// This decorator has been re-introduced 4 times by bulk OnPush codemods and has
// broken the Import DCF flow every time (symptom: modal stuck, "mandatory
// parameters missing" / "Feature parameter Missing" popup on save). The component
// mutates non-signal fields (featureFile, showFileName, deviceInfo,
// startConfigurationData, softwareVer, deviceDataFromMetaData, newDevice, etc.)
// from .subscribe() and setTimeout() callbacks with no markForCheck. See repo
// memory (angular-21-upgrade.md) for full history.
@Component({
  standalone: false,
  selector: "app-configure-device-modal",
  templateUrl: "./configure-device-modal.component.html",
  styleUrls: ["./configure-device-modal.component.scss"],
})
export class ConfigureDeviceModalComponent implements OnInit, OnDestroy {
  @Input() public deviceInfo: LegacyAny;
  @Input() public featureInfo: LegacyAny;

  luminaireValues = [...LUMINAIRE_FEATURE_VALUES];
  lightControlValues = [...LIGHT_CONTROL_FEATURE_VALUES];
  luminaireInfoValues = [...LUMINAIRE_INFO_FEATURE_VALUES];
  featureFile: unknown;
  private readonly showDragAreaSignal = signal(false);
  missingFieldsErrorMsg: string;
  // De-duplicates DCF-import error popups when getDeviceSoftwareVersion
  // errors and the device-search returns [] for the same import.
  private dcfImportErrorShown = false;
  isLuminaire = 0;
  isLightControl = 0;
  isLuminaireInfo = 0;
  countChecked = 0;
  private readonly isDisabledButtonSignal = signal(true);
  private readonly disableCheckboxSignal = signal(true);
  schemaResp: LegacyAny[];
  private readonly loadingSignal = signal(false);
  private readonly customLoaderSignal = signal(true);
  featureArray: string[] = [];
  filteredFeatureArray: string[] = [];
  private readonly buttonDisableSignal = signal(false);
  feature12NcName: string[] = [];
  private readonly ZTV_FEATURE_NAME = '0-10v / 1-10v';
  private readonly STARTUP_TIME_FEATURE_NAME = 'Startup time';
  ztvFeatureSelected: string | boolean;
  private readonly fileLoadSignal = signal(false);
  newConfigId: string | null = null;
  private readonly ismissingfiledsSignal = signal(false);
  filteredItems: LegacyAny[] = [];
  trimSoftwareVersion = "";
  searchName: DeviceSearchItem[] = [];
  filteredDeviceList: NamedDevice[] = [];
  selectedDevice: string | null = null;
  searchTerm: string = "";

  get showDragArea(): boolean { return this.showDragAreaSignal(); }
  set showDragArea(value: boolean) { this.showDragAreaSignal.set(value); }

  get isDisabledButton(): boolean { return this.isDisabledButtonSignal(); }
  set isDisabledButton(value: boolean) { this.isDisabledButtonSignal.set(value); }

  get disableCheckbox(): boolean { return this.disableCheckboxSignal(); }
  set disableCheckbox(value: boolean) { this.disableCheckboxSignal.set(value); }

  get loading(): boolean { return this.loadingSignal(); }
  set loading(value: boolean) { this.loadingSignal.set(value); }

  get customLoader(): boolean { return this.customLoaderSignal(); }
  set customLoader(value: boolean) { this.customLoaderSignal.set(value); }

  get buttonDisable(): boolean { return this.buttonDisableSignal(); }
  set buttonDisable(value: boolean) { this.buttonDisableSignal.set(value); }

  get fileLoad(): boolean { return this.fileLoadSignal(); }
  set fileLoad(value: boolean) { this.fileLoadSignal.set(value); }

  get ismissingfileds(): boolean { return this.ismissingfiledsSignal(); }
  set ismissingfileds(value: boolean) { this.ismissingfiledsSignal.set(value); }

  private appendFeatureLabel(featureKey: keyof typeof CONFIGURE_DEVICE_FEATURE_LABELS | string): void {
    this.featureArray.push(CONFIGURE_DEVICE_FEATURE_LABELS[featureKey] || featureKey);
  }
  lumin0SoftwareVersionSchema: string;
  lumin3SoftwareVersionSchema: string;
  lumin4SoftwareVersionSchema: string;
  lumin5SoftwareVersionSchema: string;
  currentDate: string | null;
  newDeviceList: NamedDevice[] = [];
  softwareVerTrim: LegacyAny;
  deviceSelected: boolean = false;
  isRelaod: boolean = false;
  isFeatureAvailable: boolean;
  readingStatus: unknown;
  directSubscription: unknown;
  regions: string[] = [];
  regionStr: string = "";
  apiList: unknown[] = [];
  dtlFeatureSelected: string | boolean;
  minDimLevelFeatureSelected: string | boolean;
  deviceDataFromMetaData: LegacyAny;
  corridorFeatureSelected: string | boolean;
  lineswitchFeatureSelected: string | boolean;
  minDimAlwaysChecked = false;
  dtlSoftwareVersionSchema: string;
  flextuneSoftwareVersionSchema: string;
  mindimlevelSoftwareVersionSchema: string;
  dalipsuSoftwareVersionSchema: string;
  daliaddressing102SoftwareVersionSchema: string;
  dali102and207ConfigSoftwareVersionSchema: string;
  auxSoftwareVersionSchema: string;
  lsiSoftwareVersionSchema: string;
  aocmultichannelSoftwareVersionSchema: string;
  ampdimSoftwareVersionSchema: string;
  lineswitchSoftwareVersionSchema: string;
  owpSoftwareVersionSchema: string;
  codedmainsSoftwareVersionSchema: string;
  mtpSoftwareVersionSchema: string;
  dimminginterfaceSoftwareVersionSchema: string;
  latestDeviceVersion: string[] = [];
  latestDvValue = "";
  softwareVer: LegacyAny = [];
  selectedFeature: unknown;
  featuresArraySelected: { selectedFeaturesInfo: string[] } | null = null;
  ztvSoftwareVersionSchema: string;
  ztvoutputSoftwareVersionSchema: string;
  lipSoftwareVersionSchema: string;
  rsoSoftwareVersionSchema: string;
  aocSoftwareVersionSchema: string;
  touchdimSoftwareVersionSchema: string;
  diia253maintenanceSoftwareVersionSchema: string;
  corridorSoftwareVersionSchema: string;
  astSoftwareVersionSchema: string;
  cloSoftwareVersionSchema: string;
  multiConfigSoftwareSchema: string;
  cloliteSoftwareVersionSchema: string;
  dynadimmerSoftwareVersionSchema: string;
  dcemergencySoftwareVersionSchema: string;
  aloFeatureSelected: string | boolean;
  aocFeatureSelected: string | boolean;
  cloFeatureSelected: string | boolean;
  psucloFeatureSelected: string | boolean;
  luminoFeatureSelected: string | boolean;
  softwareVersionSchema: string;
  otpSoftwareVersionSchema: string;
  dscSoftwareVersionSchema: string;
  lowmainslockSoftwareVersionSchema: string;
  dipswitchSoftwareVersionSchema: string;
  alwaysChecked = false;
  cloAlwaysChecked: boolean;
  cloLiteAlwaysChecked: boolean;
  aloAlwaysChecked: boolean;
  aocAlwaysChecked: boolean;
  psuAlwaysChecked: boolean;
  LumInfoAlwaysChecked: boolean;
  zTvAlwaysChecked: boolean;
  subscription: unknown;

  startConfigForm: FormGroup;
  aloschemaConfig = {};

  dcfFileUrl = "feature/api/v1/configuration/{{configurationID}}/device-configuration-file";
  envFeatureFileUrl: string;
  envStartConfigURL;
  envScehmaLIstApi;
  aloschemaDetails =
    DEVICE_SCHEMA_DETAILS_PATH;
  envAloschemaDetails: string;
  importDCF = "feature/api/v1/configuration/";

  startConfigurationData = {};
  isFeatureSelected = false;
  isFeatureChecked = true;
  isDeviceVersionMismatch = false;
  alreadySelectedFeature: unknown;
  configurationData: LegacyAny;
  files: File[] = [];
  fileName: string;
  showFileName = false;
  flag = 0;
  newDevice: unknown;
  existing = false;
  deviceSearch: unknown;
  is12nc: boolean;
  envDeviceListURL;
  deviceList: DeviceSearchItem[] = [];
  url: string;
  fullDeviceList = DEVICE_SEARCH_ENDPOINT;
  keyword = "device12nc";
  nameKeyword = "deviceName";
  searchTypes = [
    { id: 1, name: "12nc" },
    { id: 2, name: "productname" },
  ];
  isName: boolean;
  enableButton: boolean;
  metaDataAPILink = "feature/api/v1/getMetaData";
  textContent: string;
  deviceData: LegacyAny;
  importLinkFeature: string;
  configID: string;
  isError = false;
  private isDestroyed = false;

  unsubscribe = new Subject<void>();

  startupFeatureSelected: string | boolean;
  startupAlwaysChecked: boolean;
  DTLAlwaysChecked: boolean;
  corridorAlwaysChecked: boolean;
  dcemergencyAlwaysChecked: boolean;
  dcemergencyFeatureSelected: string | boolean;
  touchDimAlwaysChecked: boolean;
  touchDimFeatureSelected: string | boolean;
  loadingUpload: boolean;
  featuresData: unknown[] = [];
  pathVal: string;
  public service: LegacyAny;
  constructor(

    private readonly datePipe: DatePipe,
    public activemodal: NgbActiveModal,
    private readonly fb: FormBuilder,
    private readonly http: HttpClient,
    public route: Router,
    private readonly modalService: NgbModal,
    private readonly appState: AppStateService,
    private readonly utils: FeatureUtilsService,
    private readonly deviceConfig: DeviceConfigStateService,
    private readonly availFeat: AvailableFeaturesStateService,
    private readonly uiSignals: UiSignalsStateService,
    private readonly httpClient: MultiOneHttpClient,
    private readonly clo: CloStateService,
    private readonly userPrefs: UserPreferencesStateService,
    private readonly aloAoc: AloAocStateService
  ) {
    this.service = {
      getdirectFeature: this.availFeat.getdirectFeature,
      makeDisable: this.uiSignals.makeDisable,
      sendCloseConfigPopup: (value: string) => this.uiSignals.sendCloseConfigPopup(value),
      checkCloseConfigPopup: this.uiSignals.checkCloseConfigPopup,
      apiCall: (...args: unknown[]) =>
        (this.httpClient as unknown as { apiCall: (...params: unknown[]) => unknown }).apiCall(...args),
      getSupportedSchemaList: (...args: unknown[]) =>
        (this.httpClient as unknown as { getSupportedSchemaList: (...params: unknown[]) => unknown }).getSupportedSchemaList(...args),
      getConfigurationAPI: (...args: unknown[]) =>
        (this.httpClient as unknown as { getConfigurationAPI: (...params: unknown[]) => unknown }).getConfigurationAPI(...args),
      getStartConfiguration: (...args: unknown[]) =>
        (this.httpClient as unknown as { getStartConfiguration: (...params: unknown[]) => unknown }).getStartConfiguration(...args),
      generatefile: (...args: unknown[]) =>
        (this.httpClient as unknown as { generatefile: (...params: unknown[]) => unknown }).generatefile(...args),
      setConfigurationData: (...args: unknown[]) =>
        (this.appState as unknown as { setConfigurationData: (...params: unknown[]) => void }).setConfigurationData(...args),
      setCloLoadConfigureExtraValidation: (...args: unknown[]) =>
        (this.clo as unknown as { setCloLoadConfigureExtraValidation: (...params: unknown[]) => void }).setCloLoadConfigureExtraValidation(...args)
    };
  }

  ngOnInit() {
    window.addEventListener('dragover', this.preventDefaultWindow, false);
    window.addEventListener('drop', this.preventDefaultWindow, false);
    this.pathVal = this.utils.getImageUrl(window.location.hostname);
    this.deviceConfig.getTriggerConfigureDevice.subscribe((val) => {
      if (val) {
        this.configureDevice();
      }
    });
    this.flag = 1;
    const currentDate = new Date();
    this.currentDate = this.datePipe.transform(currentDate, "dd-MM-yyyy");
    // this.service.getEnvValue();
    // this.service.getEnvData
    //   .pipe(takeUntil(this.unsubscribe))
    //   .subscribe((url) => {
    //     this.url = url;
    //   });

    this.url = environment.multiOneAPIUrl;
    this.service.getdirectFeature.subscribe((data) => {
      if (data === "success") {
        this.configureDevice();
      }
    });

    this.service.makeDisable.subscribe((data) => {
      if (data === "confirm") {
        this.deviceSelected = true;
      }
    });
    this.service.sendCloseConfigPopup("");
    setTimeout(() => {
      this.service.checkCloseConfigPopup
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((resp) => {
          if (resp === "close") {
            this.activemodal.close();
          }
        });
    }, 100);

    this.startConfigForm = this.fb.group({
      selectedFeatures: this.fb.array([]),
    });
    this.subscription = this.availFeat.getconfigData.subscribe((data) => {
      this.featureFile = data;
      if (this.featureFile === "configData") {
        this.configurationData = this.appState.getDevice12nc();
        if (
          this.deviceInfo &&
          this.deviceInfo.selectedFeaturesInfo !== undefined
        ) {
          this.alreadySelectedFeature =
            this.deviceInfo.selectedFeaturesInfo.forEach((element) => {
              this.onChange(element);
            });
        }
        const checkedDevicePopup: Record<string, unknown> | null = this.appState.getStoreOmitOption();
        const compareLatest = this.appState.getCompareFirmwareNoLatest();
        if (compareLatest) {
          this.softwareVer = compareLatest;
        }
        if (
          compareLatest == null &&
          checkedDevicePopup &&
          checkedDevicePopup["storeDevicePopupValue"]
        ) {
          // && checkedDevicePopup['storeDevicePopupCheck'] === true){
        }
        const compareFirmware = this.appState.getCompareFirmwareNoLatest();
        if (compareFirmware) {
          this.softwareVerTrim = compareFirmware;
          if (this.softwareVerTrim.length > 3) {
            this.softwareVerTrim = this.softwareVerTrim.slice(0, -2);
          }
        } else {
          this.getSoftwareVersion();
        }
        setTimeout(() => {
        if (this.softwareVer.length > 3) {
          if (Array.isArray(this.softwareVer)) {
            this.softwareVerTrim = this.softwareVer[0].slice(0, -2);
          } else {
            this.softwareVerTrim = this.softwareVer.slice(0, -2);
          }
          }

          this.schemalist();
        }, 2500);
        this.latestDvValue = this.latestDeviceVersion[0];
      }

      if (this.featureFile === "featureData") {
        this.featuresArraySelected = this.appState.getConfigurationData();
        this.deviceInfo = this.featuresArraySelected;
        for (
          let i = 0;
          i < this.featuresArraySelected.selectedFeaturesInfo.length;
          i++
        ) {
          if (this.featuresArraySelected.selectedFeaturesInfo[i] === "Alo") {
            this.aloFeatureSelected =
              this.featuresArraySelected.selectedFeaturesInfo[i];
          }
        }
        for (
          let j = 0;
          j < this.featuresArraySelected.selectedFeaturesInfo.length;
          j++
        ) {
          if (this.featuresArraySelected.selectedFeaturesInfo[j] === "Aoc") {
            this.aocFeatureSelected =
              this.featuresArraySelected.selectedFeaturesInfo[j];
          }
        }
        for (
          let k = 0;
          k < this.featuresArraySelected.selectedFeaturesInfo.length;
          k++
        ) {
          if (
            this.featuresArraySelected.selectedFeaturesInfo[k] === "Clo" ||
            this.featuresArraySelected.selectedFeaturesInfo[k] === "CloLite" ||
            this.featuresArraySelected.selectedFeaturesInfo[k] === "Clolite"
          ) {
            this.cloFeatureSelected =
              this.featuresArraySelected.selectedFeaturesInfo[k];
          }
        }
        this.featuresArraySelected.selectedFeaturesInfo.forEach((feature) => {
          if (matchesFeatureSelectionAlias(feature, "DTL")) {
            this.dtlFeatureSelected = feature;
          }
          if (matchesFeatureSelectionAlias(feature, "Min Dim Level")) {
            this.minDimLevelFeatureSelected = feature;
          }
        });
        //Min Dim Level End
        for (
          let k = 0;
          k < this.featuresArraySelected.selectedFeaturesInfo.length;
          k++
        ) {
          if (this.featuresArraySelected.selectedFeaturesInfo[k] === "Psu") {
            this.psucloFeatureSelected =
              this.featuresArraySelected.selectedFeaturesInfo[k];
          }
        }
        for (
          let k = 0;
          k < this.featuresArraySelected.selectedFeaturesInfo.length;
          k++
        ) {
          if (
            this.featuresArraySelected.selectedFeaturesInfo[k] === "LumInfo"
          ) {
            this.luminoFeatureSelected =
              this.featuresArraySelected.selectedFeaturesInfo[k];
          }
        }
        for (
          let k = 0;
          k < this.featuresArraySelected.selectedFeaturesInfo.length;
          k++
        ) {
          if (this.featuresArraySelected.selectedFeaturesInfo[k] === "clo") {
            this.luminoFeatureSelected =
              this.featuresArraySelected.selectedFeaturesInfo[k];
          }
        }
        for (
          let k = 0;
          k < this.featuresArraySelected.selectedFeaturesInfo.length;
          k++
        ) {
          if (
            this.featuresArraySelected.selectedFeaturesInfo[k] ===
            this.ZTV_FEATURE_NAME
          ) {
            this.ztvFeatureSelected =
              this.featuresArraySelected.selectedFeaturesInfo[k];
          }
        }
        for (const feature of this.featuresArraySelected.selectedFeaturesInfo) {
          if (feature === this.STARTUP_TIME_FEATURE_NAME) {
            this.startupFeatureSelected = feature;
          }
        }
        for (
          let k = 0;
          k < this.featuresArraySelected.selectedFeaturesInfo.length;
          k++
        ) {
          if (
            this.featuresArraySelected.selectedFeaturesInfo[k] === "Corridor"
          ) {
            this.corridorFeatureSelected =
              this.featuresArraySelected.selectedFeaturesInfo[k];
          }
        }
        for (
          let k = 0;
          k < this.featuresArraySelected.selectedFeaturesInfo.length;
          k++
        ) {
          if (
            this.featuresArraySelected.selectedFeaturesInfo[k] === "Line Switch"
          ) {
            this.lineswitchFeatureSelected =
              this.featuresArraySelected.selectedFeaturesInfo[k];
          }
        }
        for (
          let k = 0;
          k < this.featuresArraySelected.selectedFeaturesInfo.length;
          k++
        ) {
          if (
            this.featuresArraySelected.selectedFeaturesInfo[k] ===
            "DC Emergency"
          ) {
            this.dcemergencyFeatureSelected =
              this.featuresArraySelected.selectedFeaturesInfo[k];
          }
        }

        for (
          let k = 0;
          k < this.featuresArraySelected.selectedFeaturesInfo.length;
          k++
        ) {
          if (
            this.featuresArraySelected.selectedFeaturesInfo[k] ===
              "Touch and Dim" ||
            this.featuresArraySelected.selectedFeaturesInfo[k] === "touchdim" ||
            this.featuresArraySelected.selectedFeaturesInfo[k] === "touchdim-"
          ) {
            this.touchDimFeatureSelected =
              this.featuresArraySelected.selectedFeaturesInfo[k];
          }
        }
      }

      if (this.featureFile === "readDevice") {
        this.loading = true;
        this.getDeviceReader().subscribe((resp) => {
          if (resp) {
            this.readingStatus = resp;
            this.loading = false;
          }
        });
      }
    });

    this.isFeatureSelected = this.startConfigForm.value.selectedFeatures.length;
    this.deviceSearch = "first";
    // For select Feature Story
    // setTimeout(() => {
    //   if(Array.isArray(this.softwareVer)) {
    //     this.trimSoftwareVersion = this.softwareVer[0];
    //     this.trimSoftwareVersion = this.trimSoftwareVersion.slice(0, -2);
    //   } else {
    //     this.trimSoftwareVersion = this.softwareVer.slice(0, -2);
    //   }
    // }, 500);

    this.uiSignals.getConfigMethod.subscribe((data) => {
      if (data === "success") {
        this.confirm();
      }
    });
  }

  onSearch(term: string): void {
    this.searchTerm = term;
     if(!term)
     {
      this.filteredDeviceList = this.newDeviceList;
     }
      else {
      this.filteredDeviceList = this.newDeviceList.filter(device =>
        device.name.toLowerCase().includes(term.toLowerCase())
      );
    }
  }
  highlightMatch(text: string, search: string): string {
    if (!search) return text;
    const re = new RegExp(`(${search})`, 'gi');
    return text.replace(re, '<b>$1</b>');
  }

  getSoftwareVersion() {
    this.loading = true;
    if (this.featureFile === "uploadDCF") {
      this.configurationData = this.deviceInfo.device12nc;
    }
    this.resetSoftwareVersionCollections();
    // this.service.getEnvValue();
    // this.subscription = this.service.getEnvData
    //   .pipe(takeUntil(this.unsubscribe))
    //   .subscribe((url) => {
    //     if (this.url.length) {
    //       this.unsubscribe.next(undefined);
          this.envAloschemaDetails = this.url;
          this.getDeviceSoftwareVersion().subscribe((deviceVersion) => {
            // for (let i = 0; i < deviceVersion['items'].length; i++) {
            //   for(let j = 0; j < deviceVersion['items'][i].softwareVersions.length; j++) {
            //     latestVersion.push(deviceVersion['items'][i].softwareVersions[j]);
            //     this.latestDeviceVersion.push(deviceVersion['items'][i].deviceType.nameAndVersion.split(':')[2]);
            //   }
            // }
            deviceVersion["items"].forEach(function (arrayItem) {
              for (const key in arrayItem) {
                if (!arrayItem.hasOwnProperty("softwareVersions")) {
                  delete arrayItem[key];
                }
              }
            });
            deviceVersion["items"] = deviceVersion["items"].filter((ele) => {
              return ele.constructor === Object && Object.keys(ele).length > 0;
            });
            const collectedSoftwareVersions: string[] = [];
            const collectedDeviceVersions: string[] = [];
            for (let i = deviceVersion["items"].length - 1; i >= 0; i--) {
              for (
                let j = deviceVersion["items"][i].softwareVersions.length - 1;
                j >= 0;
                j--
              ) {
                const swVersion = String(deviceVersion["items"][i].softwareVersions[j]);
                collectedSoftwareVersions.push(swVersion);
                const resolvedDeviceVersion = this.extractDeviceVersionCandidate(
                  deviceVersion["items"][i]?.deviceType?.nameAndVersion
                );
                if (resolvedDeviceVersion) {
                  collectedDeviceVersions.push(resolvedDeviceVersion);
                }
              }
            }
            const ascSoftwareVersions = collectedSoftwareVersions
              .map((a) =>
                a
                  .split(".")
                  .map((n) => +n + 100000)
                  .join(".")
              )
              .sort((a, b) => (a > b ? 1 : -1))
              .map((a) =>
                a
                  .split(".")
                  .map((n) => +n - 100000)
                  .join(".")
              );
            ascSoftwareVersions.reverse();
            this.softwareVerTrim = [];
            this.softwareVer = ascSoftwareVersions;
            this.latestDeviceVersion = collectedDeviceVersions;

            const url = window.location.href;
            this.latestDvValue = this.latestDeviceVersion[0];
            this.softwareVer.forEach((element) => {
              const ver = element.slice(0, -2);
              this.softwareVerTrim.push(ver);
            });
            if (this.softwareVerTrim.length > 1 && url.includes("deviceList")) {
              this.softwareVerTrim = this.softwareVerTrim[0];
              this.latestDvValue = this.latestDeviceVersion[0];
            }
            this.appState.setCompareFirmwareNoLatest(this.softwareVer[0]);
            this.loading = false;
          });
      //   }
      // });
  }

  getDeviceReader() {
    return this.service.apiCall("get", DEVICE_STATUS_ENDPOINT);
  }
  getDevice() {

          this.loading = true;
          this.envDeviceListURL =
            this.url + this.fullDeviceList + "?showArchive=true";
          this.regions = [...DEFAULT_DEVICE_REGIONS];
          const htHeader = new HttpHeaders()
            .set("region", this.regions[0])
            .append("region", this.regions[1])
            .append("region", this.regions[2])
            .append("region", this.regions[3]);
          this.apiList = this.service.apiCall(
            "get",
            this.envDeviceListURL,
            "",
            { headers: htHeader }
          );
          this.unsubscribe.next(undefined);
          this.newDeviceList = [];
          forkJoin(this.apiList).subscribe((resp) => {
            if (resp) {
              this.deviceList = [].concat.apply([], resp);
              this.deviceList.forEach((element) => {
                this.newDeviceList.push({
                  name: `${element.device12nc} ${element.deviceName}`,
                });
              });
              this.filteredDeviceList=this.newDeviceList;
              this.loading = false;
            }
          });

  }

  searchWithAllDevice12Nc(searchText: string, onComplete?: () => void, onError?: (err: unknown) => void) {
    this.regions = [...DEFAULT_DEVICE_REGIONS];
    const htHeader = new HttpHeaders()
      .set("region", this.regions[0])
      .append("region", this.regions[1])
      .append("region", this.regions[2])
      .append("region", this.regions[3]);
    this.searchTextValue(searchText, htHeader);
    this.unsubscribe.next(undefined);
    this.searchName = [];
    forkJoin(this.apiList).subscribe({
      next: (resp) => {
        this.searchName = [].concat.apply([], resp);
        this.appState.setReaderDeviceFullInfo(this.searchName);
        if (onComplete) {
          onComplete();
        }
      },
      error: (err) => {
        if (onError) {
          onError(err);
        }
      }
    });
  }

  onRetry() {
    this.showDragArea = true;
    this.isDeviceVersionMismatch = false;
    this.ismissingfileds = false;
    this.showFileName = false; // Reset file name display if needed
    this.fileLoad = false;     // Reset file load state if needed
  }

  searchTextValue(searchText: string, headers: HttpHeaders) {
    if (this.url.length) {
      this.loading = true;
      this.envDeviceListURL =
        this.url + this.fullDeviceList + "?device12nc=";
      this.unsubscribe.next(undefined);
      this.apiList = this.service.apiCall(
        "get",
        this.envDeviceListURL + searchText + "&showArchive=true",
        "",
        { headers: headers }
      );
    }
  }

  getDeviceSearchList() {
    return this.service.apiCall("get", this.envDeviceListURL);
  }

  onSubmit() {
    this.loading = true;
    this.isFeatureSelected = false;
    const tmp = this.getUniqueSelectedFeatures();

    if (this.url.length) {
      this.handleConfigurationAPIFlow(tmp);
    } else {
      this.handleStartConfigurationFlow(tmp);
    }
  }

  private getUniqueSelectedFeatures(): string[] {
    const tmp: string[] = [];
    for (const feature of this.startConfigForm.value.selectedFeatures) {
      if (tmp.indexOf(feature) === -1) {
        tmp.push(feature);
      }
    }
    return tmp;
  }

  private handleConfigurationAPIFlow(tmp: string[]): void {
    this.updateSoftwareVersionFromState();
    this.updateDeviceInfoPicture();
    this.unsubscribe.next(undefined);
    this.envAloschemaDetails = this.url;

    const transformedFeatures = this.transformFeatureNamesToAPI(tmp);
    const configurationID = this.appState.getConfigId();

    this.service
      .getConfigurationAPI(this.envAloschemaDetails, configurationID)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (resp) => {
          this.proceedToConfigurationData(transformedFeatures, resp, configurationID);
        },
        error: (err) => {
          this.handleConfigurationAPIError(err);
        }
      });
  }

  private handleStartConfigurationFlow(tmp: string[]): void {
    this.updateSoftwareVersionFromState();
    this.unsubscribe.next(undefined);
    this.envStartConfigURL = this.url;
    this.updateDeviceInfoPicture();
    this.updateDeviceInfoFromConfig();
    this.normalizeSoftwareVersion();

    const mockDevice = this.createMockDevice();
    this.service
      .getStartConfiguration(mockDevice, this.envStartConfigURL)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (data) => {
          this.handleStartConfigurationSuccess(data, tmp);
        },
        error: (error) => {
          this.handleStartConfigurationError(error);
        }
      });
  }

  private updateSoftwareVersionFromState(): void {
    const compareFirmware = this.appState.getCompareFirmwareNoLatest();
    if (compareFirmware) {
      this.softwareVer = compareFirmware;
    }
  }

  private updateDeviceInfoPicture(): void {
    if (this.deviceInfo) {
      const domain = EASY_DESIGN_API_DOMAIN;
      this.deviceInfo.devicetypePicture = this.deviceInfo.devicetypePicture
        ? this.deviceInfo.devicetypePicture
        : this.deviceInfo.picture;
      if (this.deviceInfo.devicetypePicture != undefined) {
        this.deviceInfo.devicetypePicture =
          domain + this.deviceInfo.devicetypePicture.replace(domain, "");
      }
    }
  }

  private transformFeatureNamesToAPI(features: string[]): string[] {
    const tmp = [...features];
    const transformations = [
      { from: this.STARTUP_TIME_FEATURE_NAME, to: "Ast" },
      { from: this.ZTV_FEATURE_NAME, to: "Ztv" },
      { from: "LumInfo", to: "LumInfoFormat" },
      { from: "Psu", to: "SrPsu" }
    ];

    transformations.forEach(({ from, to }) => {
      const index = tmp.indexOf(from);
      if (index > -1) {
        tmp.splice(index, 1);
        tmp.push(to);
      }
    });

    return tmp;
  }

  private handleConfigurationAPIError(err: unknown): void {
    const typedErr = err as { description?: string; message?: string };
    this.loading = false;
    this.showDragArea = false;
    this.showFileName = false;
    this.ismissingfileds = true;
    this.missingFieldsErrorMsg = typedErr?.description || "Unknown error";

    const modalRef = this.modalService.open(ErrorComponent, {
      keyboard: false,
      centered: true,
      windowClass: ERROR_MODAL_WINDOW_CLASS,
      backdrop: MODAL_BACKDROP_STATIC,
    });
    modalRef.componentInstance.errorName = typedErr?.message || "Error";
    modalRef.componentInstance.errorDescription = typedErr?.description || "Unknown error";
  }

  private updateDeviceInfoFromConfig(): void {
    const dataFromConfig = this.appState.getConfigurationData();
    if (dataFromConfig) {
      this.deviceInfo = {
        deviceName: dataFromConfig.deviceName,
        device12nc: dataFromConfig.device12nc,
        softwareVerSchema: dataFromConfig.softwareVerSchema,
        picture: dataFromConfig.picture,
        luminSoftVer0: dataFromConfig.luminSoftVer0,
        luminSoftVer3: dataFromConfig.luminSoftVer3,
        luminSoftVer4: dataFromConfig.luminSoftVer4,
        luminSoftVer5: dataFromConfig.luminSoftVer5,
        dtlSoftVer: dataFromConfig.dtlSoftVer,
        flextuneSoftVer: dataFromConfig.flextuneSoftVer,
        mindimlevelSoftVer: dataFromConfig.mindimlevelSoftVer,
        mtpSoftVer: dataFromConfig.mtpSoftVer,
        dimminginterfaceSoftVer: dataFromConfig.dimminginterfaceSoftVer,
        dynadimmerSoftVer: dataFromConfig.dynadimmerSoftVer,
        dcemergencySoftVer: dataFromConfig.dcemergencySoftVer,
        ztvSoftVer: dataFromConfig.ztvSoftVer,
        ztvoutputSoftVer: dataFromConfig.ztvoutputSoftVer,
        lipSoftVer: dataFromConfig.lipSoftVer,
        rsoSoftVer: dataFromConfig.rsoSoftVer,
        aocSoftVer: dataFromConfig.aocSoftVer,
        astSoftVer: dataFromConfig.astSoftVer,
        cloliteSoftVer: dataFromConfig.cloliteSoftVer,
        touchdimSoftVer: dataFromConfig.touchdimSoftVer,
        corridorSoftVer: dataFromConfig.corridorSoftVer,
        cloSoftVer: dataFromConfig.cloSoftVer,
        multiconfigSoftVer: dataFromConfig.multiconfigSoftVer,
        ampdimSoftVer: dataFromConfig.ampdimSoftVer,
        lineswitchSoftVer: dataFromConfig.lineswitchSoftVer,
        owpSoftVer: dataFromConfig.owpSoftVer,
        codedmainsSoftVer: dataFromConfig.codedmainsSoftVer,
        selectedFeaturesInfo: dataFromConfig.selectedFeaturesInfo,
        dali102AddressingSoftVer: dataFromConfig.dali102AddressingSoftVer,
        dali102and207ConfigSoftVer: dataFromConfig.dali102and207ConfigSoftVer,
        auxSoftVer: dataFromConfig.auxSoftVer,
        lsiSoftVer: dataFromConfig.lsiSoftVer,
        aocmultichannelSoftVer: dataFromConfig.aocSoftVer,
        otpSoftVer: dataFromConfig.otpSoftVer,
        feature12Nc: dataFromConfig.feature12Nc,
        resp: dataFromConfig.resp,
      };
      this.softwareVer = dataFromConfig.softwareVersion;
    }
  }

  private normalizeSoftwareVersion(): void {
    if (Array.isArray(this.softwareVer)) {
      this.softwareVer = this.softwareVer[0];
    }
    if (this.softwareVer.length > 3) {
      this.softwareVer = this.softwareVer.slice(0, -2);
    }
  }

  private createMockDevice(): { device12nc: string; deviceName: string; softwareVersion: string } {
    return {
      device12nc: this.deviceInfo.device12nc,
      deviceName: this.deviceInfo.deviceName,
      softwareVersion: String(this.softwareVer),
    };
  }

  private handleStartConfigurationSuccess(data: { configurationID: string }, tmp: string[]): void {
    this.appState.removeDeviceVersion();
    const configurationID = this.appState.getConfigId() || data["configurationID"];

    if (this.softwareVer.length > 3) {
      this.softwareVer = this.softwareVer.slice(0, -2);
    }

    this.buildStartConfigurationData(configurationID, tmp);
    this.assignSoftwareVersionSchemas();
    this.mergeRespData();
    this.assignRemainingConfigData();
    this.saveConfigurationAndNavigate();
  }

  private buildStartConfigurationData(configurationID: string, tmp: string[]): void {
    this.startConfigurationData["configurationID"] = configurationID;
    this.newConfigId = configurationID;
    this.appState.setConfigId(configurationID);
    this.startConfigurationData["deviceName"] = this.deviceInfo["deviceName"];
    this.startConfigurationData["softwareVersion"] = this.softwareVer;
    this.startConfigurationData["selectedFeaturesInfo"] =
      tmp.length > 0 ? tmp : this.deviceInfo.selectedFeaturesInfo;
    this.startConfigurationData["device12nc"] = this.deviceInfo.device12nc;
  }

  private assignSoftwareVersionSchemas(): void {
    const schemaMapping = [
      { key: "softwareVerSchema", schema: this.softwareVersionSchema, fallback: this.deviceInfo.softwareVerSchema },
      { key: "luminSoftVer0", schema: this.lumin0SoftwareVersionSchema, fallback: this.deviceInfo.luminSoftVer0 },
      { key: "luminSoftVer3", schema: this.lumin3SoftwareVersionSchema, fallback: this.deviceInfo.luminSoftVer3 },
      { key: "luminSoftVer4", schema: this.lumin4SoftwareVersionSchema, fallback: this.deviceInfo.luminSoftVer4 },
      { key: "luminSoftVer5", schema: this.lumin5SoftwareVersionSchema, fallback: this.deviceInfo.luminSoftVer5 },
      { key: "dtlSoftVer", schema: this.dtlSoftwareVersionSchema, fallback: this.deviceInfo.dtlSoftVer },
      { key: "flextuneSoftVer", schema: this.flextuneSoftwareVersionSchema, fallback: this.deviceInfo.flextuneSoftVer },
      { key: "mindimlevelSoftVer", schema: this.mindimlevelSoftwareVersionSchema, fallback: this.deviceInfo.mindimlevelSoftVer },
      { key: "ampdimSoftVer", schema: this.ampdimSoftwareVersionSchema, fallback: this.deviceInfo.ampdimSoftVer },
      { key: "lineswitchSoftVer", schema: this.lineswitchSoftwareVersionSchema, fallback: this.deviceInfo.lineswitchSoftVer },
      { key: "owpSoftVer", schema: this.owpSoftwareVersionSchema, fallback: this.deviceInfo.owpSoftVer },
      { key: "codedmainsSoftVer", schema: this.codedmainsSoftwareVersionSchema, fallback: this.deviceInfo.codedmainsSoftVer },
      { key: "dalipsuSoftVer", schema: this.dalipsuSoftwareVersionSchema, fallback: this.deviceInfo.dalipsuSoftVer },
      { key: "dali102AddressingSoftVer", schema: this.daliaddressing102SoftwareVersionSchema, fallback: this.deviceInfo.dali102AddressingSoftVer },
      { key: "dali102and207ConfigSoftVer", schema: this.dali102and207ConfigSoftwareVersionSchema, fallback: this.deviceInfo.dali102and207ConfigSoftVer },
      { key: "auxSoftVer", schema: this.auxSoftwareVersionSchema, fallback: this.deviceInfo.auxSoftVer },
      { key: "lsiSoftVer", schema: this.lsiSoftwareVersionSchema, fallback: this.deviceInfo.lsiSoftVer },
      { key: "aocmultichannelSoftVer", schema: this.aocmultichannelSoftwareVersionSchema, fallback: this.deviceInfo.aocmultichannelSoftVer },
      { key: "otpSoftVer", schema: this.otpSoftwareVersionSchema, fallback: this.deviceInfo.otpSoftVer },
      { key: "dscSoftVer", schema: this.dscSoftwareVersionSchema, fallback: this.deviceInfo.dscSoftVer },
      { key: "lowmainslockSoftVer", schema: this.lowmainslockSoftwareVersionSchema, fallback: this.deviceInfo.lowmainslockSoftVer },
      { key: "dipswitchSoftVer", schema: this.dipswitchSoftwareVersionSchema, fallback: this.deviceInfo.dipswitchSoftVer },
      { key: "mtpSoftVer", schema: this.mtpSoftwareVersionSchema, fallback: this.deviceInfo.mtpSoftVer },
      { key: "dimminginterfaceSoftVer", schema: this.dimminginterfaceSoftwareVersionSchema, fallback: this.deviceInfo.dimminginterfaceSoftVer },
      { key: "dynadimmerSoftVer", schema: this.dynadimmerSoftwareVersionSchema, fallback: this.deviceInfo.dynadimmerSoftVer },
      { key: "dcemergencySoftVer", schema: this.dcemergencySoftwareVersionSchema, fallback: this.deviceInfo.dcemergencySoftVer },
      { key: "cloSoftVer", schema: this.cloSoftwareVersionSchema, fallback: this.deviceInfo.cloSoftVer },
      { key: "multiconfigSoftVer", schema: this.multiConfigSoftwareSchema, fallback: this.deviceInfo.multiconfigSoftVer },
      { key: "astSoftVer", schema: this.astSoftwareVersionSchema, fallback: this.deviceInfo.astSoftVer },
      { key: "cloliteSoftVer", schema: this.cloliteSoftwareVersionSchema, fallback: this.deviceInfo.cloliteSoftVer },
      { key: "aocSoftVer", schema: this.aocSoftwareVersionSchema, fallback: this.deviceInfo.aocSoftVer },
      { key: "touchdimSoftVer", schema: this.touchdimSoftwareVersionSchema, fallback: this.deviceInfo.touchdimSoftVer },
      { key: "diia253maintenanceSoftVer", schema: this.diia253maintenanceSoftwareVersionSchema, fallback: this.deviceInfo.diia253maintenanceSoftVer },
      { key: "corridorSoftVer", schema: this.corridorSoftwareVersionSchema, fallback: this.deviceInfo.corridorSoftVer },
      { key: "ztvSoftVer", schema: this.ztvSoftwareVersionSchema, fallback: this.deviceInfo.ztvSoftVer },
      { key: "ztvoutputSoftVer", schema: this.ztvoutputSoftwareVersionSchema, fallback: this.deviceInfo.ztvoutputSoftVer },
      { key: "lipSoftVer", schema: this.lipSoftwareVersionSchema, fallback: this.deviceInfo.lipSoftVer },
      { key: "rsoSoftVer", schema: this.rsoSoftwareVersionSchema, fallback: this.deviceInfo.rsoSoftVer }
    ];

    schemaMapping.forEach(({ key, schema, fallback }) => {
      this.startConfigurationData[key] = schema ? schema : fallback;
    });
  }

  private mergeRespData(): void {
    this.deviceInfo.resp = this.deviceInfo.resp ? this.deviceInfo.resp : {};
    const existingConfigData = this.appState.getConfigurationData();
    const existingSaveSetData = this.appState.getSaveSetData();

    if (existingConfigData && existingConfigData["resp"]) {
      this.startConfigurationData["resp"] = existingConfigData["resp"];
    } else if (existingSaveSetData && existingSaveSetData["resp"]) {
      this.startConfigurationData["resp"] = existingSaveSetData["resp"];
    } else {
      this.startConfigurationData["resp"] = this.deviceInfo.resp;
    }
  }

  private assignRemainingConfigData(): void {
    this.startConfigurationData["picture"] = this.deviceInfo.devicetypePicture
      ? this.deviceInfo.devicetypePicture
      : this.deviceInfo.picture;
    this.startConfigurationData["feature12Nc"] =
      this.feature12NcName.length > 0
        ? this.feature12NcName
        : this.deviceInfo.feature12Nc;
  }

  private saveConfigurationAndNavigate(): void {
    this.service.setConfigurationData(this.startConfigurationData);
    const saveDataCopy = JSON.parse(JSON.stringify(this.startConfigurationData));
    this.appState.setSaveSetData(saveDataCopy);
    this.appState.setSaveSetData1(saveDataCopy);
    this.appState.forcePersistState();

    this.loading = false;
    this.isFeatureSelected = true;
    this.activemodal.close();
    this.appState.removeDeviceVersion();
    this.loadFFStandardMode();
    this.route.navigate(["/featureLanding"]);

    this.handleFeatureLandingReload();
  }

  private handleFeatureLandingReload(): void {
    const url = window.location.href;
    if (url.includes("/featureLanding") && !this.isRelaod) {
      const removeResp = this.appState.getConfigurationData();
      if (removeResp["resp"]["CLO"]) {
        delete removeResp["resp"]["CLO"];
      } else if (removeResp["resp"]["CLOLite"]) {
        delete removeResp["resp"]["CLOLite"];
      }
      this.service.setConfigurationData(removeResp);
      this.appState.forcePersistState();
      window.location.reload();
    }
  }

  private handleStartConfigurationError(error: unknown): void {
    const typedErr = error as { message?: string; description?: string };
    this.loading = false;
    const modalRef = this.modalService.open(ErrorComponent, {
      keyboard: false,
      centered: true,
      windowClass: ERROR_MODAL_WINDOW_CLASS,
      backdrop: MODAL_BACKDROP_STATIC,
    });
    modalRef.componentInstance.errorName = typedErr["message"];
    modalRef.componentInstance.errorDescription = typedErr["description"];
  }

  proceedToConfigurationData(tmp, resp, configurationID) {
    if (this.featuresData && this.featuresData["failedFeatures"]) {
      this.featuresData["failedFeatures"].forEach((element) => {
        const normalizedMessage = String(element?.["message"] || "")
          .split(" ")
          .join("")
          .toLowerCase()
          .trim();
        if (
          normalizedMessage === INVALID_DATA_MESSAGE
        ) {
          if (element.hasOwnProperty("featureParameters")) {
            resp[element["featureName"]] = element["featureParameters"][0];
          }
        }
      });
    }
    this.startConfigurationData["configurationID"] = configurationID;
    this.startConfigurationData["deviceName"] = this.deviceInfo["deviceName"];
    this.startConfigurationData["softwareVersion"] =
      this.deviceInfo["softwareVersion"];
    tmp.forEach((element) => {
      const index = tmp.indexOf("Ast");
      if (index > -1) {
        tmp.splice(index, 1);
        tmp.push(this.STARTUP_TIME_FEATURE_NAME);
      }
      const index1 = tmp.indexOf("Ztv");
      if (index1 > -1) {
        tmp.splice(index1, 1);
        tmp.push(this.ZTV_FEATURE_NAME);
      }
      const index2 = tmp.indexOf("LumInfoFormat");
      if (index2 > -1) {
        tmp.splice(index2, 1);
        tmp.push("LumInfo");
      }
      const index3 = tmp.indexOf("SrPsu");
      if (index3 > -1) {
        tmp.splice(index3, 1);
        tmp.push("Psu");
      }
    });
    if (this.softwareVer.length > 3) {
      this.softwareVer = this.softwareVer.slice(0, -2);
    }

    //MOWEB 1.0.1 DONE
    const supportedFeat12NC = this.deduplicateLuminfoVersions(this.removeUnSupportedFeat(this.feature12NcName));
    this.startConfigurationData["selectedFeaturesInfo"] = supportedFeat12NC;
    /* this.startConfigurationData["selectedFeaturesInfo"] =
      this.feature12NcName; */
    //MOWEB 1.0.1 DONE
    this.startConfigurationData["luminSoftVer0"] =
      this.lumin0SoftwareVersionSchema;
    this.startConfigurationData["luminSoftVer3"] =
      this.lumin3SoftwareVersionSchema;
    this.startConfigurationData["luminSoftVer4"] =
      this.lumin4SoftwareVersionSchema;
    this.startConfigurationData["luminSoftVer5"] =
      this.lumin5SoftwareVersionSchema;
    this.startConfigurationData["dtlSoftVer"] = this.dtlSoftwareVersionSchema;
    this.startConfigurationData["flextuneSoftVer"] = this.flextuneSoftwareVersionSchema;
    this.startConfigurationData["mindimlevelSoftVer"] =
      this.mindimlevelSoftwareVersionSchema;
    this.startConfigurationData["ampdimSoftVer"] =
      this.ampdimSoftwareVersionSchema;
    this.startConfigurationData["lineswitchSoftVer"] =
      this.lineswitchSoftwareVersionSchema;
    this.startConfigurationData["owpSoftVer"] = this.owpSoftwareVersionSchema;
    this.startConfigurationData["codedmainsSoftVer"] =
      this.codedmainsSoftwareVersionSchema;
    this.startConfigurationData["dalipsuSoftVer"] =
      this.dalipsuSoftwareVersionSchema;
    this.startConfigurationData["dali102AddressingSoftVer"]=this.daliaddressing102SoftwareVersionSchema;
    this.startConfigurationData["dali102and207ConfigSoftVer"]=this.dali102and207ConfigSoftwareVersionSchema;
    this.startConfigurationData["auxSoftVer"] = this.auxSoftwareVersionSchema;
    this.startConfigurationData["lsiSoftVer"] = this.lsiSoftwareVersionSchema;
    this.startConfigurationData["aocmultichannelSoftVer"] = this.aocmultichannelSoftwareVersionSchema;
    this.startConfigurationData["otpSoftVer"] = this.otpSoftwareVersionSchema;
    this.startConfigurationData["dscSoftVer"] = this.dscSoftwareVersionSchema;
    this.startConfigurationData["lowmainslockSoftVer"] = this.lowmainslockSoftwareVersionSchema;
    this.startConfigurationData["dipswitchSoftVer"] = this.dipswitchSoftwareVersionSchema;
    this.startConfigurationData["mtpSoftVer"] = this.mtpSoftwareVersionSchema;
    this.startConfigurationData["dimminginterfaceSoftVer"] =
      this.dimminginterfaceSoftwareVersionSchema;
    this.startConfigurationData["dynadimmerSoftVer"] =
      this.dynadimmerSoftwareVersionSchema;
    this.startConfigurationData["dcemergencySoftVer"] =
      this.dcemergencySoftwareVersionSchema;
    this.startConfigurationData["ztvSoftVer"] = this.ztvSoftwareVersionSchema;
    this.startConfigurationData["ztvoutputSoftVer"] =
      this.ztvoutputSoftwareVersionSchema;
    this.startConfigurationData["lipSoftVer"] = this.lipSoftwareVersionSchema;
    this.startConfigurationData["rsoSoftVer"] = this.rsoSoftwareVersionSchema;
    this.startConfigurationData["aocSoftVer"] = this.aocSoftwareVersionSchema;
    this.startConfigurationData["touchdimSoftVer"] =
      this.touchdimSoftwareVersionSchema;
    this.startConfigurationData["diia253maintenanceSoftVer"] =
      this.diia253maintenanceSoftwareVersionSchema;
    this.startConfigurationData["corridorSoftVer"] =
      this.corridorSoftwareVersionSchema;
    this.startConfigurationData["astSoftVer"] = this.astSoftwareVersionSchema;
    this.startConfigurationData["cloliteSoftVer"] =
      this.cloliteSoftwareVersionSchema;
    this.startConfigurationData["cloSoftVer"] = this.cloSoftwareVersionSchema;
    this.startConfigurationData["multiconfigSoftVer"] = this.multiConfigSoftwareSchema;
    this.startConfigurationData["softwareVersion"] = this.softwareVer;
    this.startConfigurationData["softwareVerSchema"] =
      this.softwareVersionSchema;
    this.startConfigurationData["device12nc"] = this.deviceInfo.device12nc;
    this.startConfigurationData["feature12Nc"] = this.feature12NcName;
    this.startConfigurationData["picture"] = this.deviceInfo.devicetypePicture;

    // Prioritize existing configured data to prevent data loss on refresh
    // Only use API resp as fallback when no existing configuration exists

    // Merge resp: Start with API resp, then overlay with user-configured values
    // This ensures user configurations take precedence over API defaults
    const existingConfigData = this.appState.getConfigurationData();
    const existingSaveSetData = this.appState.getSaveSetData();
    const existingResp =
      (existingConfigData && existingConfigData["resp"]) ||
      (existingSaveSetData && existingSaveSetData["resp"]) ||
      {};
    // Keep missing legacy keys from existing resp, but ensure imported/API
    // values win to prevent stale CLO/LumInfo data after DCF import.
    this.startConfigurationData["resp"] = {
      ...existingResp,
      ...(resp || {}),
    };

    const deviceObj = {
      device12nc: this.startConfigurationData["device12nc"],
      softwareVersion: this.startConfigurationData["softwareVersion"],
    };
    this.service
      .getStartConfiguration(deviceObj, this.url)
      .subscribe((data1) => {
        this.startConfigurationData["configurationID"] =
          data1["configurationID"];
        this.service.setConfigurationData(this.startConfigurationData);
        // Create deep copy for SaveSetData
        const saveDataCopy = JSON.parse(JSON.stringify(this.startConfigurationData));
        this.appState.setSaveSetData(saveDataCopy);
        this.appState.setSaveSetData1(saveDataCopy);
        // Force immediate save to sessionStorage before navigation
        this.appState.forcePersistState();
        this.availFeat.setConfigurationData(this.startConfigurationData);
        this.loading = false;
        this.isFeatureSelected = true;
        this.activemodal.close();
        this.appState.removeDeviceVersion();

        // localStorage.setItem('loadFeatureFile',"true");
        // this.uiSignals.sendModes('Advanced')
        const hasCloLite = !!(this.startConfigurationData["resp"].CLOLite);
        const hasClo = !!(this.startConfigurationData["resp"].CLO);
        this.appState.setLoadffflow("true");
        this.loadFFStandardMode();
        this.route.navigate(["/featureLanding"]).then(() => {
          if (hasCloLite) {
            this.service.setCloLoadConfigureExtraValidation("Configure_CLOLite");
          }
          if (hasClo) {
            this.service.setCloLoadConfigureExtraValidation("Configure_CLO");
          }
        });
      }, (err) => {
        this.loading = false;
        const modalRef = this.modalService.open(ErrorComponent, {
          keyboard: false,
          centered: true,
          windowClass: ERROR_MODAL_WINDOW_CLASS,
          backdrop: MODAL_BACKDROP_STATIC,
        });
        modalRef.componentInstance.errorName = err["message"];
        modalRef.componentInstance.errorDescription = err["description"];
      });
  }

  private deduplicateLuminfoVersions(features: string[]): string[] {
    let luminfoAdded = false;
    return features.filter((element) => {
      if (/^luminfoformat\d*-?$/.test(element)) {
        if (luminfoAdded) {
          return false;
        }
        luminfoAdded = true;
      }
      return true;
    });
  }

  removeUnSupportedFeat(feature12NcName: string[]): string[] {
    const supportFeatData: string[] = [];
    feature12NcName.forEach((element) => {
      if (
        !(
          // element === "ztv-" ||
          (
            element === "ampdim-" ||
            element === "owp-" ||
            //element === "lineswitch-" ||
            element === "codedmains-" ||
            element === "mtp-" ||
            //element === "dynadimmer-" ||
            element === "luminfoformat1-" ||
            //element === "luminfoformat0-" ||
            element === "dcemergency-" ||
            element === "mindimlevel-" ||
            // element === "dimminginterface-" ||
            element === "dtl-" ||
            element === "corridor-"
          )
          //|| element === "touchdim-"
        )
      ) {
        supportFeatData.push(element);
      }
    });
    return supportFeatData;
  }

  removeId() {
    this.appState.setConfigId(null);
    // localStorage.removeItem('configurationData');
    // localStorage.removeItem('comparefirmwarenoLatest');
  }

  removeVer() {
    const ver = this.appState.getSetLastSoftwareVersion();
    if (ver) {
      this.appState.setCompareFirmwareNoLatest(ver);
    }
  }

  getConfigurationNumber() {
    const compareFirmware = this.appState.getCompareFirmwareNoLatest();
    if (compareFirmware) {
      this.softwareVer = compareFirmware;
    }
    this.loading = true;
    this.showFileName = true;
    if (this.url.length) {
      this.unsubscribe.next(undefined);
      this.envStartConfigURL = this.url;
      const mockDevice = {
        device12nc: this.deviceInfo?.device12nc,
        deviceName: this.deviceInfo?.deviceName,
        softwareVersion: this.softwareVer,
      };
      this.service
        .getStartConfiguration(mockDevice, this.envStartConfigURL)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: (data) => {
            this.startConfigurationData["configurationID"] =
              data["configurationID"];
            this.appState.setConfigId(
              this.startConfigurationData["configurationID"]
            );
            this.startConfigurationData["deviceName"] =
              this.deviceInfo["deviceName"];
            this.startConfigurationData["softwareVersion"] =
              this.deviceInfo["softwareVersion"];
            this.startConfigurationData["softwareVerSchema"] =
              this.softwareVersionSchema;
            this.startConfigurationData["device12nc"] =
              this.deviceInfo.device12nc;
            this.startConfigurationData["feature12Nc"] = this.feature12NcName;
            this.service.setConfigurationData(this.startConfigurationData);
            this.DCFImport();
            // this.loading=false;
            // this.isFeatureSelected=true;
          },
          error: (err) => {
            // Guard against late-emitted errors arriving after the component
            // (and its injector) has been destroyed; touching NgbModal here
            // would otherwise throw NG0205 ("Injector has already been destroyed").
            if (this.isDestroyed) {
              return;
            }
            this.loading = false;
            this.showFileName = false;
            this.fileLoad = false;
            this.ismissingfileds = true;
            this.missingFieldsErrorMsg = err["description"];
            const modalRef = this.modalService.open(ErrorComponent, {
              keyboard: false,
              centered: true,
              windowClass: ERROR_MODAL_WINDOW_CLASS,
              backdrop: MODAL_BACKDROP_STATIC,
            });
            modalRef.componentInstance.errorName = err["message"];
            modalRef.componentInstance.errorDescription = err["description"];
          }
        });
    }
  }

  DCFImport() {
    this.loading = true;
    if (this.url.length) {
      this.unsubscribe.next();
      this.importLinkFeature = this.url + this.importDCF;
      const modes = this.appState.getModes();
      this.service
        .apiCall(
          "post",
          this.importLinkFeature +
            this.startConfigurationData["configurationID"] +
            "/device-configuration-file",
          this.textContent
        )
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: (data) => {
            this.featuresData = data;
            this.appState.setImportFeatureData(data);
            const successfulFeatureCount = data["successfulFeatures"]?.length || 0;
            const failedFeatureCount = data["failedFeatures"]?.length || 0;
            const isStandardOrDefaultMode =
              modes === CONFIGURATION_MODES[0] || modes === STANDARD_CONFIGURATION_MODE;
            if (
              successfulFeatureCount > 0 &&
              failedFeatureCount === 0 &&
              isStandardOrDefaultMode
            ) {
              this.isError = true;
            }
            if (
              successfulFeatureCount > 0 &&
              failedFeatureCount > 0
            ) {
              if (this.newDevice) {
                this.isError = true;
                const modalRef = this.modalService.open(
                  SelectingDeviceModalComponent,
                  {
                    backdrop: "static",
                    keyboard: false,
                    windowClass: "select-device-modal-comp",
                    centered: true,
                  }
                );
                modalRef.componentInstance.device = this.deviceInfo;
                this.availFeat.modalConfigurationData("uploadDCF");
                this.userPrefs.sendValidationMessage("success");
              }
            }
            if (
              successfulFeatureCount > 0 &&
              failedFeatureCount > 0 &&
              isStandardOrDefaultMode
            ) {
              if (!this.newDevice) {
                this.isError = true;
                const modalRef = this.modalService.open(
                  FeatureFilePartialCompatibleModalComponent,
                  {
                    backdrop: "static",
                    keyboard: false,
                    windowClass: "select-device-modal-comp",
                    centered: true,
                  }
                );
                modalRef.componentInstance.device = this.deviceInfo;
                modalRef.componentInstance.hasSuccessfulFeatures = true;
                this.uiSignals.sendSelectedModala("showFFUploadModal");
                this.availFeat.modalConfigurationData("uploadDCF");
                this.userPrefs.sendValidationMessage("success");
              }
            }
            if (
              failedFeatureCount > 0 &&
              successfulFeatureCount === 0 &&
              isStandardOrDefaultMode
            ) {
              let failedFetureWithInvalidData = false;
              data["failedFeatures"].forEach((element) => {
                if (
                  element["message"]
                    .split(" ")
                    .join("")
                    .toLowerCase()
                    .trim() !== INVALID_DATA_MESSAGE
                ) {
                  failedFetureWithInvalidData = true;
                }
              });
              if (failedFetureWithInvalidData) {
                if (!this.newDevice) {
                  this.isError = false;
                  const modalRef = this.modalService.open(
                    FeatureFilePartialCompatibleModalComponent,
                    {
                      backdrop: "static",
                      keyboard: false,
                      windowClass: "select-device-modal-comp",
                      centered: true,
                    }
                  );
                  modalRef.componentInstance.device = this.deviceInfo;
                  modalRef.componentInstance.hasSuccessfulFeatures = false;
                  this.uiSignals.sendSelectedModala("showFFUploadModal");
                  this.availFeat.modalConfigurationData("uploadDCF");
                  this.userPrefs.sendValidationMessage("failed");
                }
              }
            }
            if (
              failedFeatureCount > 0 &&
              successfulFeatureCount === 0
            ) {
              if (this.newDevice) {
                this.isError = false;
                const modalRef = this.modalService.open(
                  SelectingDeviceModalComponent,
                  {
                    backdrop: "static",
                    keyboard: false,
                    windowClass: "select-device-modal-fail",
                    centered: true,
                  }
                );
                modalRef.componentInstance.device = this.deviceInfo;
                this.availFeat.modalConfigurationData("uploadDCF");
                this.userPrefs.sendValidationMessage("failed");
              }
            }
            const shouldKeepLoaderForAutoConfigure = !!(
              successfulFeatureCount > 0 &&
              failedFeatureCount === 0 &&
              modes === STANDARD_CONFIGURATION_MODE &&
              this.newDevice
            );
            this.loading = shouldKeepLoaderForAutoConfigure;
            this.fileLoad = shouldKeepLoaderForAutoConfigure;
            this.configureDevice();
          },
          error: (err) => {
            this.fileLoad = false;
            this.loading = false;
            this.showFileName = false;
            this.ismissingfileds = true;
            this.missingFieldsErrorMsg = err["description"];
            const modalRef = this.modalService.open(ErrorComponent, {
              keyboard: false,
              centered: true,
              windowClass: ERROR_MODAL_WINDOW_CLASS,
              backdrop: MODAL_BACKDROP_STATIC,
            });
            modalRef.componentInstance.errorName = err["message"];
            modalRef.componentInstance.errorDescription = err["description"];
          }
        });
    }
  }

  private updateFeatureCheckState(feature: string, checked: boolean): void {
    const featureMap = {
      'Clo': 'cloAlwaysChecked',
      'Alo': 'aloAlwaysChecked',
      'Aoc': 'aocAlwaysChecked',
      'Psu': 'psuAlwaysChecked',
      'LumInfo': 'LumInfoAlwaysChecked',
      [this.ZTV_FEATURE_NAME]: 'zTvAlwaysChecked',
      'CloLite': 'cloLiteAlwaysChecked',
      'Clolite': 'cloLiteAlwaysChecked',
      [this.STARTUP_TIME_FEATURE_NAME]: 'startupAlwaysChecked',
      'Min Dim Level': 'minDimAlwaysChecked',
      'DTL': 'DTLAlwaysChecked',
      'Dtl': 'DTLAlwaysChecked',
      'Corridor': 'corridorAlwaysChecked',
      'DC Emergency': 'dcemergencyAlwaysChecked',
      'Touch and Dim': 'touchDimAlwaysChecked',
      'touchdim': 'touchDimAlwaysChecked'
    };

    const propertyName = featureMap[feature];
    if (propertyName) {
      this[propertyName] = checked;
    }
  }

  onChange(feature, checked?, option?) {
    this.selectedFeature = feature;
    if (checked === undefined) {
      checked = true;
    }
    this.updateFeatureCheckState(feature, checked);
    const selectedFeaturesArray = <FormArray>(
      this.startConfigForm.controls.selectedFeatures
    );
    if (checked) {
      selectedFeaturesArray.push(new FormControl(feature));
    } else {
      const index = selectedFeaturesArray.controls.findIndex(
        (x) => x.value === feature
      );
      selectedFeaturesArray.removeAt(index);
    }
    this.isFeatureSelected = this.startConfigForm.value.selectedFeatures.length;
  }

  private extractDeviceVersionCandidate(value: unknown): string {
    if (typeof value !== "string") {
      return "";
    }
    const trimmed = value.trim();
    if (!trimmed) {
      return "";
    }
    const parts = trimmed.split(":");
    return parts.length > 2 ? (parts[2] || "").trim() : trimmed;
  }

  private resolveSchemaDeviceVersion(): string {
    const candidates: unknown[] = [
      this.latestDvValue,
      Array.isArray(this.latestDeviceVersion) ? this.latestDeviceVersion[0] : "",
      this.filteredItems?.[0]?.deviceType?.nameAndVersion,
    ];

    for (const candidate of candidates) {
      const resolved = this.extractDeviceVersionCandidate(candidate);
      if (resolved) {
        return resolved;
      }
    }

    return "";
  }

  private resetSoftwareVersionCollections(): void {
    this.softwareVer = [];
    this.latestDeviceVersion = [];
  }

  schemalistApi() {
    if (this.deviceInfo) {
      const resolvedDeviceVersion = this.resolveSchemaDeviceVersion();
      if (resolvedDeviceVersion) {
        this.latestDvValue = resolvedDeviceVersion;
        return this.service.getSupportedSchemaList(
          this.envScehmaLIstApi,
          this.deviceInfo.device12nc,
          resolvedDeviceVersion
        );
      }

      // return this.httpClient.apiCall('get',this.envScehmaLIstApi+'?12nc='+this.deviceInfo.device12nc+'&'+'Software Version='+this.softwareVer);
    }

    return undefined;
  }
  schemalist(onLoaded?: () => void) {
    this.feature12NcName = [];
    this.customLoader = true;
          this.envScehmaLIstApi = this.url;
          const schemaListRequest = this.schemalistApi();
          if (schemaListRequest) {
            schemaListRequest.subscribe({
              next: (resp) => {
                this.startConfigurationData["readerCapabilities"] = decodeReaderCapabilities(resp) ?? null;
                this.schemaResp = resp["metadata"].schemas;
                const tags = resp["metadata"].tags;
                if(tags && tags.length > 0){
                  this.appState.setTags(tags[0]);
                }else{
                  this.appState.setTags(null);
                }
                this.isFeatureAvailable = false;
                this.schemaResp.forEach((element, index) => {
                  const birth =
                    element.match(/[^,]*mcsa-[^,]*/g) ||
                    element.match(/[^,]*birth-[^,]*/g) ||
                    element.match(/[^,]*public-[^,]*/g) ||
                    element.match(/[^,]*bl[^,]*/g) ||
                    element.match(/[^,]*''[^,]*/g);
                  if (!birth) {
                    const splitted = element.split(":")[1];
                    if (splitted) {
                      const feature12NCvalue = splitted.substring(
                        0,
                        splitted.length - this.deviceInfo.device12nc.length
                      );
                      this.feature12NcName.push(feature12NCvalue);
                    }
                  }
                });
                this.feature12NcName = this.feature12NcName.filter(Boolean);
                this.startConfigurationData["allFeature12Nc"] = [...new Set(this.feature12NcName)];
                this.customLoader = false;
                const val = this.schemaResp.toString();
                this.featureArray = [];
                const versiontoString =
                  val.match(/[^,]*alo[^,]*/g) ||
                  val.match(/[^,]*clo[^,]*/g) ||
                  val.match(/[^,]*aoc[^,]*/g) ||
                  val.match(/[^,]*ztv[^,]*/g);
                if (versiontoString) {
                  this.softwareVersionSchema = versiontoString
                    .toString()
                    .split(":")[2];
                }
                if (val.match(/[^,]*alo[^,]*/g)) {
                  this.appendFeatureLabel("alo");
                }
                if (val.match(/[^,]*clo-[^,]*/g)) {
                  if (
                    val
                      .match(/[^,]*clo-[^,]*/g)
                      .toString()
                      .split(":")[1]
                      .slice(
                        0,
                        val
                          .match(/[^,]*clo-[^,]*/g)
                          .toString()
                          .split(":")[1]
                          .indexOf(this.deviceInfo.device12nc)
                      ) === "clo-"
                  ) {
                    const cloversiontoString = val.match(/[^,]*clo-[^,]*/g);
                    this.cloSoftwareVersionSchema = cloversiontoString
                      .toString()
                      .split(":")[2];
                    this.appendFeatureLabel("clo");
                  }
                }
                if (val.match(/[^,]*aoc[^,]*/g)) {
                  this.appendFeatureLabel("aoc");
                  const aocversiontoString = val.match(/[^,]*aoc[^,]*/g);
                  this.aocSoftwareVersionSchema = aocversiontoString
                    .toString()
                    .split(":")[2];
                }
                if (val.match(/[^,]*ast[^,]*/g)) {
                  const astversiontoString = val.match(/[^,]*ast[^,]*/g);
                  this.astSoftwareVersionSchema = astversiontoString
                    .toString()
                    .split(":")[2];
                  this.featureArray.push(this.STARTUP_TIME_FEATURE_NAME);
                  this.isLightControl = this.isLightControl + 1;
                }
                if (val.match(/[^,]*corridor[^,]*/g)) {
                  const corridorversiontoString =
                    val.match(/[^,]*corridor[^,]*/g);
                  this.corridorSoftwareVersionSchema = corridorversiontoString
                    .toString()
                    .split(":")[2];
                  this.appendFeatureLabel("corridor");
                }
                if (val.match(/[^,]*dynadimmer[^,]*/g)) {
                  const dynadimmerVersiontoString =
                    val.match(/[^,]*dynadimmer[^,]*/g);
                  this.dynadimmerSoftwareVersionSchema =
                    dynadimmerVersiontoString.toString().split(":")[2];
                  this.appendFeatureLabel("dynadimmer");
                }
                if (val.match(/[^,]*dimminginterface[^,]*/g)) {
                  const dimminginterfaceVersiontoString = val.match(
                    /[^,]*dimminginterface[^,]*/g
                  );
                  this.dimminginterfaceSoftwareVersionSchema =
                    dimminginterfaceVersiontoString.toString().split(":")[2];
                  this.appendFeatureLabel("dimminginterface");
                }
                if (val.match(/[^,]*mtp[^,]*/g)) {
                  const mtpVersiontoString = val.match(/[^,]*mtp[^,]*/g);
                  this.mtpSoftwareVersionSchema = mtpVersiontoString
                    .toString()
                    .split(":")[2];
                  this.appendFeatureLabel("mtp");
                }
                if (val.match(/[^,]*ampdim[^,]*/g)) {
                  const ampdimVersiontoString = val.match(/[^,]*ampdim[^,]*/g);
                  this.ampdimSoftwareVersionSchema = ampdimVersiontoString
                    .toString()
                    .split(":")[2];
                  this.appendFeatureLabel("ampdim");
                }
                if (val.match(/[^,]*lineswitch[^,]*/g)) {
                  const lineswitchVersiontoString =
                    val.match(/[^,]*lineswitch[^,]*/g);
                  this.lineswitchSoftwareVersionSchema =
                    lineswitchVersiontoString.toString().split(":")[2];
                  this.appendFeatureLabel("lineswitch");
                }
                if (val.match(/[^,]*owp[^,]*/g)) {
                  const owpVersiontoString = val.match(/[^,]*owp[^,]*/g);
                  this.owpSoftwareVersionSchema = owpVersiontoString
                    .toString()
                    .split(":")[2];
                  this.appendFeatureLabel("owp");
                }
                if (val.match(/[^,]*codedmains[^,]*/g)) {
                  const codedmainsVersiontoString =
                    val.match(/[^,]*codedmains[^,]*/g);
                  this.codedmainsSoftwareVersionSchema =
                    codedmainsVersiontoString.toString().split(":")[2];
                  this.appendFeatureLabel("codedmains");
                }
                if (val.match(/[^,]*dalipsu[^,]*/g)) {
                  const dalipsuVersiontoString = val.match(/[^,]*dalipsu[^,]*/g);
                  this.dalipsuSoftwareVersionSchema = dalipsuVersiontoString
                    .toString()
                    .split(":")[2];
                  this.appendFeatureLabel("dalipsu");
                }
                if (val.match(/[^,]*daliaddressing102[^,]*/g)) {
                  const daliaddressing102VersiontoString = val.match(/[^,]*daliaddressing102[^,]*/g);
                  this.daliaddressing102SoftwareVersionSchema = daliaddressing102VersiontoString
                    .toString()
                    .split(":")[2];
                  this.appendFeatureLabel("daliaddressing102");
                }
                if (val.match(/[^,]*daliconfiguration102and207[^,]*/g)) {
                  const daliconfiguration102and207VersiontoString = val.match(/[^,]*daliconfiguration102and207[^,]*/g);
                  this.dali102and207ConfigSoftwareVersionSchema = daliconfiguration102and207VersiontoString
                    .toString()
                    .split(":")[2];
                  this.appendFeatureLabel("daliconfiguration102and207");
                }
                if (val.match(/[^,]*aux[^,]*/g)) {
                  const auxVersiontoString = val.match(/[^,]*aux[^,]*/g);
                  this.auxSoftwareVersionSchema = auxVersiontoString
                    .toString()
                    .split(":")[2];
                  this.appendFeatureLabel("aux");
                }
                if (val.match(/[^,]*lsi[^,]*/g)) {
                  const lsiVersiontoString = val.match(/[^,]*lsi[^,]*/g);
                  this.lsiSoftwareVersionSchema = lsiVersiontoString
                    .toString()
                    .split(":")[2];
                  this.appendFeatureLabel("lsi");
                }
                if (val.match(/[^,]*aocmultichannel[^,]*/g)) {
                  const aocmultichannelVersiontoString = val.match(/[^,]*aocmultichannel[^,]*/g);
                  this.aocmultichannelSoftwareVersionSchema = aocmultichannelVersiontoString
                    .toString()
                    .split(":")[2];
                  this.appendFeatureLabel("aocmultichannel");
                }
                if (val.match(/[^,]*otp[^,]*/g)) {
                  const otpVersiontoString = val.match(/[^,]*otp[^,]*/g);
                  this.otpSoftwareVersionSchema = otpVersiontoString
                    .toString()
                    .split(":")[2];
                  this.appendFeatureLabel("otp");
                }
                if (val.match(/[^,]*dsc[^,]*/g)) {
                  const dscVersiontoString = val.match(/[^,]*dsc[^,]*/g);
                  this.dscSoftwareVersionSchema = dscVersiontoString
                    .toString()
                    .split(":")[2];
                  this.appendFeatureLabel("dsc");
                }
                if (val.match(/[^,]*lowmainslock[^,]*/g)) {
                  const lowmainslockVersiontoString = val.match(/[^,]*lowmainslock[^,]*/g);
                  this.lowmainslockSoftwareVersionSchema = lowmainslockVersiontoString
                    .toString()
                    .split(":")[2];
                  this.appendFeatureLabel("lowmainslock");
                }
                if (val.match(/[^,]*dipswitch[^,]*/g)) {
                  const dipswitchVersiontoString = val.match(/[^,]*dipswitch[^,]*/g);
                  this.dipswitchSoftwareVersionSchema = dipswitchVersiontoString
                    .toString()
                    .split(":")[2];
                  this.appendFeatureLabel("dipswitch");
                }

                if (val.match(/[^,]*srpsu[^,]*/g)) {
                  this.appendFeatureLabel("srpsu");
                }

                if (val.match(/[^,]*luminfoformat0[^,]*/g)) {
                  const luminversiontoString = val.match(/[^,]*luminfoformat0[^,]*/g);
                  this.lumin0SoftwareVersionSchema = luminversiontoString
                    .toString()
                    .split(':')[2];
                }

                if (val.match(/[^,]*luminfoformat3[^,]*/g)) {
                  const luminversiontoString = val.match(/[^,]*luminfoformat3[^,]*/g);
                  this.lumin3SoftwareVersionSchema = luminversiontoString
                    .toString()
                    .split(':')[2];
                }

                if (val.match(/[^,]*luminfoformat4[^,]*/g)) {
                  const luminversiontoString = val.match(/[^,]*luminfoformat4[^,]*/g);
                  this.lumin4SoftwareVersionSchema = luminversiontoString
                    .toString()
                    .split(':')[2];
                }

                if (val.match(/[^,]*luminfoformat5[^,]*/g)) {
                  const luminversiontoString = val.match(/[^,]*luminfoformat5[^,]*/g);
                  this.lumin5SoftwareVersionSchema = luminversiontoString
                    .toString()
                    .split(':')[2];
                }

                if (val.match(/[^,]*luminfo[^,]*/g)) {
                  this.appendFeatureLabel("luminfo");
                  this.isLuminaireInfo = 1;
                }

                if (val.match(/[^,]*ztv[^,]*/g)) {
                  this.featureArray.push(this.ZTV_FEATURE_NAME);
                  const ztvversiontoString = val.match(/[^,]*ztv[^,]*/g);
                  this.ztvSoftwareVersionSchema = ztvversiontoString
                    .toString()
                    .split(":")[2];
                  this.isLightControl = this.isLightControl + 1;
                }
                if (val.match(/[^,]*ztvoutput[^,]*/g)) {
                  this.appendFeatureLabel("ztvoutput");
                  const ztvoutputversiontoString =
                    val.match(/[^,]*ztvoutput[^,]*/g);
                  this.ztvoutputSoftwareVersionSchema = ztvoutputversiontoString
                    .toString()
                    .split(":")[2];
                  this.isLightControl = this.isLightControl + 1;
                }
                if (val.match(/[^,]*lip[^,]*/g)) {
                  this.appendFeatureLabel("lip");
                  const lipversiontoString = val.match(/[^,]*lip[^,]*/g);
                  if (lipversiontoString.length > 1) {
                    this.lipSoftwareVersionSchema = lipversiontoString[1]
                      .toString()
                      .split(":")[2];
                  } else {
                    this.lipSoftwareVersionSchema = lipversiontoString
                      .toString()
                      .split(":")[2];
                  }
                  this.isLightControl = this.isLightControl + 1;
                }
                if (val.match(/[^,]*rso[^,]*/g)) {
                  this.appendFeatureLabel("rso");
                  const rsoversiontoString = val.match(/[^,]*rso[^,]*/g);
                  this.rsoSoftwareVersionSchema = rsoversiontoString
                    .toString()
                    .split(":")[2];
                  this.isLightControl = this.isLightControl + 1;
                }
                if (val.match(/[^,]*dtl[^,]*/g)) {
                  const dtlVersiontoString = val.match(/[^,]*dtl[^,]*/g);
                  this.dtlSoftwareVersionSchema = dtlVersiontoString
                    .toString()
                    .split(":")[2];
                  this.appendFeatureLabel("dtl");
                  this.isLightControl = this.isLightControl + 1;
                }
                if (val.match(/[^,]*flextune[^,]*/g)) {
                  const flextuneVersiontoString = val.match(/[^,]*flextune[^,]*/g);
                  this.flextuneSoftwareVersionSchema = flextuneVersiontoString
                    .toString()
                    .split(":")[2];
                  this.appendFeatureLabel("flextune");

                  this.isLightControl = this.isLightControl + 1;
                }
                if (val.match(/[^,]multiconfigmode[^,]*/g)) {
                  const multiconfigVersiontoString = val.match(/[^,]*multiconfigmode[^,]*/g);
                  this.multiConfigSoftwareSchema = multiconfigVersiontoString
                    .toString()
                    .split(":")[2];
                  this.appendFeatureLabel("multiconfigmode");

                  this.isLightControl = this.isLightControl + 1;
                }
                if (val.match(/[^,]*mindimlevel[^,]*/g)) {
                  const mindimlevelVersiontoString = val.match(
                    /[^,]*mindimlevel[^,]*/g
                  );
                  this.mindimlevelSoftwareVersionSchema =
                    mindimlevelVersiontoString.toString().split(":")[2];
                  this.appendFeatureLabel("mindimlevel");
                  this.appendFeatureLabel("mindimlevel");
                  this.isLightControl = this.isLightControl + 1;
                }
                if (val.match(/[^,]*dcemergency[^,]*/g)) {
                  const dcemergencyVersiontoString = val.match(
                    /[^,]*dcemergency[^,]*/g
                  );
                  this.dcemergencySoftwareVersionSchema =
                    dcemergencyVersiontoString.toString().split(":")[2];
                  this.appendFeatureLabel("dcemergency");
                }
                if (val.match(/[^,]*mindimlevel[^,]*/g)) {
                  this.appendFeatureLabel("mindimlevel");
                  this.isLightControl = this.isLightControl + 1;
                }
                if (val.match(/[^,]*clolite[^,]*/g)) {
                  const cloliteversiontoString = val.match(/[^,]*clolite[^,]*/g);
                  this.cloliteSoftwareVersionSchema = cloliteversiontoString
                    .toString()
                    .split(":")[2];
                  this.appendFeatureLabel("clolite");
                }
                if (val.match(/[^,]*touchdim[^,]*/g)) {
                  const touchdimversiontoString =
                    val.match(/[^,]*touchdim[^,]*/g);
                  this.touchdimSoftwareVersionSchema = touchdimversiontoString
                    .toString()
                    .split(":")[2];
                  this.appendFeatureLabel("touchdim");
                }
                if (val.match(/[^,]*diia253maintenance[^,]*/g)) {
                  const diia253maintenanceversiontoString = val.match(
                    /[^,]*diia253maintenance[^,]*/g
                  );
                  this.diia253maintenanceSoftwareVersionSchema =
                    diia253maintenanceversiontoString.toString().split(":")[2];
                  this.appendFeatureLabel("diia253maintenance");
                }
                this.filteredFeatureArray = this.featureArray.filter((item) => {
                  if (
                    item === "CLO" ||
                    item === "CLO Lite" ||
                    item === "DTL" ||
                    item === "Line Switch" ||
                    item === "Corridor" ||
                    item === "DC Emergency" ||
                    item === "Min Dim Level"
                  ) {
                    this.isLightControl = this.isLightControl + 1;
                  }
                  if (item === "ALO" || item === "AOC") {
                    this.isLuminaire = this.isLuminaire + 1;
                  }
                });
                if (this.featureArray.length < 1) {
                  this.isFeatureAvailable = true;
                }
                if (onLoaded) {
                  onLoaded();
                }
              },
              error: (error) => {
                alert("No Device Found !!");
                this.goBack();
              }
            });
          } else if (onLoaded) {
            this.customLoader = false;
            onLoaded();
          }

  }



  goBack() {
    this.activemodal.close();
  }

  exportFeatureFile() {
    this.loading = true;
    this.isDisabledButton = false;
    let configurationID;
    const dataFromConfig = this.appState.getConfigurationData();
    if (dataFromConfig) {
      this.deviceInfo = dataFromConfig;
      this.softwareVer = dataFromConfig.softwareVersion;
      configurationID = this.deviceInfo.configurationID;
    } else {
      configurationID = this.featureInfo["configurationID"];
    }
          this.envFeatureFileUrl = this.url + this.dcfFileUrl;
          this.service
            .generatefile(this.envFeatureFileUrl, configurationID)
            .subscribe({
              next: (response) => {
                this.loading = false;
                this.isDisabledButton = true;
                const encryptedText = response;
                const blob: Blob = new Blob([encryptedText], {
                  type: "application/plain; charset=utf-8",
                });
                // const url= window.URL.createObjectURL(blob);
                this.uiSignals.getReload.subscribe((resp) => {
                  if (resp === "pass") {
                    this.isRelaod = true;
                  }
                });
                saveAs(
                  blob,
                  `${this.deviceInfo.device12nc}_${this.deviceInfo.deviceName}_${this.softwareVer}_${this.currentDate}.json`
                );
                if (response) {
                  // localStorage.removeItem("configurationData");
                  this.onSubmit();
                  // localStorage.removeItem("firmwarenoLatest");
                  // localStorage.removeItem("comparefirmwarenoLatest");
                  this.activemodal.close();
                  this.appState.setModes("Advanced");
                  this.appState.setFeatureFileCReation(true);
                  // this.route.navigate(['/deviceList']);
                  this.removeId();
                }
              },
              error: (err) => {
                this.loading = false;
                const modalRef = this.modalService.open(ErrorComponent, {
                  keyboard: false,
                  centered: true,
                  windowClass: ERROR_MODAL_WINDOW_CLASS,
                  backdrop: MODAL_BACKDROP_STATIC,
                });
                modalRef.componentInstance.errorName = err["message"];
                modalRef.componentInstance.errorDescription =
                  err["description"];
              }
            });
        //}
      //},
    // );
  }


  ngOnDestroy() {
    // this.subscription.unsubscribe();
    this.isDestroyed = true;
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.uiSignals.callConfigMethod("");
    this.uiSignals.sendSelectedModala("");
    this.availFeat.modalConfigurationData("");
    // ...existing code...
    window.removeEventListener('dragover', this.preventDefaultWindow, false);
    window.removeEventListener('drop', this.preventDefaultWindow, false);

  }

  getDeviceSoftwareVersion() {
    if (!this.envAloschemaDetails.startsWith("https://api.")) {
      this.envAloschemaDetails = this.envAloschemaDetails.replace("https://", "https://api.");
    }
    return this.httpClient.getDeviceSoftwareVersion(
      this.envAloschemaDetails,
      this.configurationData
    );
  }

  /**
   * handle file from browsing
   */
  openFileContent(files) {
      this.dcfImportErrorShown = false;
      this.showFileName = true;
      this.fileName = files[0].name;
      this.appState.setSaveFileName(this.fileName);
      this.appState.setSaveFileContent(this.textContent);
      this.envAloschemaDetails = this.url + this.metaDataAPILink;
      let resp = null;
      const missingFields: string[] = [];
      try {
        const data = JSON.parse(this.textContent);

        if (!data.deviceName) missingFields.push("Device Name");
        if (!data.deviceSwVersion) missingFields.push("Device Software Version");
        if (!data.device12nc) missingFields.push("Device 12NC");

        if (missingFields.length > 0) {
          this.showFileName = false;
          this.loadingUpload = false;
          //this.isDeviceVersionMismatch = true;
          this.ismissingfileds = true;
   this.showDragArea=false;
          this.fileLoad = false;
          this.missingFieldsErrorMsg = `DCF does not contains parameter(s): ${missingFields.join(", ")}\nPlease check the file and try again`;

          const modalRef = this.modalService.open(ErrorComponent, {
            keyboard: false,
            centered: true,
            windowClass: ERROR_MODAL_WINDOW_CLASS,
          });
          modalRef.componentInstance.errorName = "File content error";
          modalRef.componentInstance.errorDescription =
            `DCF does not contains parameter(s): ${missingFields.join(", ")}\n Please check the file and try again`;
          return;

        }

        resp = {
          deviceName: data.deviceName,
          deviceSoftwareVersion: data.deviceSwVersion,
          device12ncWithSoftwareVersions: {
            [data.device12nc]: [data.deviceSwVersion + ".0"]
          }
        };
      } catch (e) {
        this.showDragArea=false;
        this.showFileName = false;
        this.loadingUpload = false;
        this.ismissingfileds = true;
        this.fileLoad = false;
        this.missingFieldsErrorMsg = "Invalid file content format or corrupted file \n Please check the file and try again";
        const modalRef = this.modalService.open(ErrorComponent, {
          keyboard: false,
          centered: true,
          windowClass: ERROR_MODAL_WINDOW_CLASS,
        });
        modalRef.componentInstance.errorName = "File parsing error";
        modalRef.componentInstance.errorDescription =
          "Invalid file content format or corrupted file \n Please check the file and try again";
        return;
      }


  this.deviceDataFromMetaData = resp;
                  if (resp) {
                    this.deviceDataFromMetaData = resp;
                    this.appState.setDecryptData(this.deviceDataFromMetaData);
                    this.appState.setCompareFirmwareNoLatest(
                        this.deviceDataFromMetaData["deviceSoftwareVersion"]
                      );
                    const objectLength = Object.keys(
                      this.deviceDataFromMetaData.device12ncWithSoftwareVersions
                    ).length;
                    for (const key in this.deviceDataFromMetaData
                      .device12ncWithSoftwareVersions) {
                      if (objectLength > 1) {
                        // let version = this.deviceDataFromMetaData.device12ncWithSoftwareVersions[key].slice(0, -2);
                        this.deviceDataFromMetaData.device12ncWithSoftwareVersions[
                          key
                          ].forEach((element) => {
                          element = element.slice(0, -2);
                          if (
                            element ===
                            this.deviceDataFromMetaData["deviceSoftwareVersion"]
                          ) {
                            this.deviceData = {
                              device12nc: key,
                              deviceSoftwareVersion: element,
                              deviceName:
                              this.deviceDataFromMetaData.deviceName,
                            };
                          }
                        });
                      } else {
                        // let version = this.deviceDataFromMetaData.device12ncWithSoftwareVersions[key][0].slice(0, -2);
                        for (const version of this.deviceDataFromMetaData
                          .device12ncWithSoftwareVersions[key]) {
                          const ver = version.slice(0, -2);

                          if (
                            ver ===
                            this.deviceDataFromMetaData["deviceSoftwareVersion"]
                          ) {
                            this.deviceData = {
                              device12nc: key,
                              deviceSoftwareVersion: ver,
                              deviceName:
                              this.deviceDataFromMetaData.deviceName,
                            };
                          }
                        }
                      }
                    }
                    this.envAloschemaDetails = this.url;
                    this.configurationData = this.deviceData.device12nc;
                    this.resetSoftwareVersionCollections();
                    this.getDeviceSoftwareVersion().subscribe(
                      (deviceVersion) => {

                        deviceVersion["items"].forEach(function (arrayItem) {
                          for (const key in arrayItem) {
                            if (!arrayItem.hasOwnProperty("softwareVersions")) {
                              delete arrayItem[key];
                            }
                          }
                        });
                        deviceVersion["items"] = deviceVersion["items"].filter(
                          (ele) => {
                            return (
                              ele.constructor === Object &&
                              Object.keys(ele).length > 0
                            );
                          }
                        );
                        const collectedSoftwareVersions: string[] = [];
                        const collectedDeviceVersions: string[] = [];
                        for (
                          let i = deviceVersion["items"].length - 1;
                          i >= 0;
                          i--
                        ) {
                          for (
                            let j =
                              deviceVersion["items"][i].softwareVersions
                                .length - 1;
                            j >= 0;
                            j--
                          ) {
                            const swVersion = String(deviceVersion["items"][i].softwareVersions[j]);
                            collectedSoftwareVersions.push(swVersion);
                            const resolvedDeviceVersion = this.extractDeviceVersionCandidate(
                              deviceVersion["items"][i]?.deviceType?.nameAndVersion
                            );
                            if (resolvedDeviceVersion) {
                              collectedDeviceVersions.push(resolvedDeviceVersion);
                            }
                          }
                        }
                        this.softwareVer = collectedSoftwareVersions;
                        this.latestDeviceVersion = collectedDeviceVersions;
                        this.filteredItems = deviceVersion["items"].filter(
                          (item) => {
                            return (
                              item["softwareVersions"]
                                .toString().length ===
                              this.deviceDataFromMetaData[
                                "deviceSoftwareVersion"
                                ].length ? item["softwareVersions"]
                                .toString() ===
                              this.deviceDataFromMetaData[
                                "deviceSoftwareVersion"
                                ] : item["softwareVersions"]
                                .toString().slice(0, -2) ===
                              this.deviceDataFromMetaData[
                                "deviceSoftwareVersion"
                                ]
                            );
                          }
                        );
                        if (
                          this.filteredItems &&
                          this.filteredItems.length > 0 &&
                          this.filteredItems[0].deviceType &&
                          typeof this.filteredItems[0].deviceType.nameAndVersion === "string"
                        ) {
                          const parts = this.filteredItems[0].deviceType.nameAndVersion.split(":");
                          this.latestDvValue = parts.length > 2 ? parts[2] : "";
                        }
                      },
                      (err) => {
                        this.loadingUpload = false;
                        this.loading = false;
                        this.showFileName = false;
                        this.fileLoad = false;
                        this.ismissingfileds = true;
                        this.missingFieldsErrorMsg = err["description"];
                        if (this.dcfImportErrorShown) {
                          return;
                        }
                        this.dcfImportErrorShown = true;
                        const modalRef = this.modalService.open(ErrorComponent, {
                          keyboard: false,
                          centered: true,
                          windowClass: ERROR_MODAL_WINDOW_CLASS,
                        });
                        modalRef.componentInstance.errorName = err["message"];
                        modalRef.componentInstance.errorDescription = err["description"];
                      }
                    );
                    this.loadingUpload = false;
                    this.fileLoad = false;
                    this.onItemChange("existing");
                   }

      this.loadingUpload = false;
  }

  backToLoadFF(): void {
    this.isDeviceVersionMismatch = false;
  }
  // uploading a File Scenario
  // fileBrowseHandler(files) {
  //   localStorage.removeItem("saveSetData");
  //   localStorage.removeItem("saveSetData1");
  //   localStorage.setItem("isAloMin", JSON.stringify(false));
  //   this.loadingUpload = true;
  //   this.fileLoad = true;
  //
  //   if (files && files.length > 0 && files[0]) {
  //     var parts = files[0].name.split(".");
  //     if (
  //       parts[parts.length - 1] === "json"
  //     ) {
  //       let reader = new FileReader();
  //       reader.onload = () => {
  //         this.textContent = reader.result;
  //         this.openFileContent(files);
  //       };
  //       reader.readAsText(files[0]);
  //     } else {
  //       alert("file corrupt or format not supported");
  //       this.loadingUpload = false;
  //       this.showFileName = false;
  //     }
  //   } else {
  //     alert("No file selected or file input is invalid.");
  //     this.loadingUpload = false;
  //     this.showFileName = false;
  //   }
  // }

  fileBrowseHandler(files) {
    this.appState.setSaveSetData(null);
    this.appState.setSaveSetData1(null);
    this.appState.setIsAloMin(false);
    this.loadingUpload = true;
    this.fileLoad = true;

    if (files && files.length > 0 && files[0]) {
      const parts = files[0].name.split(".");
      if (parts[parts.length - 1] === "json") {
        const reader = new FileReader();
        reader.onload = () => {
          this.textContent = typeof reader.result === "string" ? reader.result : "";
          this.openFileContent(files);
        };
        reader.readAsText(files[0]);
      } else {
        this.loadingUpload = false;
        this.showFileName = false;
        this.ismissingfileds = true;
        this.missingFieldsErrorMsg = "File corrupt or format not supported";
        const modalRef = this.modalService.open(ErrorComponent, {
          keyboard: false,
          centered: true,
          windowClass: ERROR_MODAL_WINDOW_CLASS,
          backdrop: MODAL_BACKDROP_STATIC,
        });
        modalRef.componentInstance.errorName = "DCF Error";
        modalRef.componentInstance.errorDescription = "File corrupt or format not supported";
      }
    } else {
      this.loadingUpload = false;
      this.showFileName = false;
      this.ismissingfileds = true;
      this.missingFieldsErrorMsg = "No file selected or file input is invalid.";
      const modalRef = this.modalService.open(ErrorComponent, {
        keyboard: false,
        centered: true,
        windowClass: ERROR_MODAL_WINDOW_CLASS,
        backdrop: MODAL_BACKDROP_STATIC,
      });
      modalRef.componentInstance.errorName = "DCF Error";
      modalRef.componentInstance.errorDescription = "No file selected or file input is invalid.";
    }
  }



  searchWith12ncDevice(searchText) {
          this.envAloschemaDetails =
            `${this.url}${this.fullDeviceList}?device12nc=`;
          this.service
            .apiCall(
              "get",
              encodeURI(
                `${this.envAloschemaDetails}${searchText}&showArchive=true`
              )
            )
            .subscribe(
              (resp: unknown) => {
                if (resp) {
                  this.searchName = resp as DeviceSearchItem[];
                  this.loading = false;
                }
              },
              (error) => {
                //alert(error["description"]);
                const modalRef = this.modalService.open(ErrorComponent, {
                  keyboard: false,
                  centered: true,
                  windowClass: ERROR_MODAL_WINDOW_CLASS,
                  backdrop: MODAL_BACKDROP_STATIC,
                });
                modalRef.componentInstance.errorName = error["message"];
                modalRef.componentInstance.errorDescription =
                  error["description"];
                this.loading = false;
                this.showFileName = false;
                // this.fileLoad = false;
              }
            );
  }

  onItemChange(event) {
    if (event === "new") {
      this.newDevice = true;
      this.is12nc = true;
      this.isError = false;
    } else {
      this.fileLoad = true;
      this.newDevice = false;
      this.isDisabledButton = false;
      this.isName = false;
      this.is12nc = false;
      this.enableButton = false;
      this.isError = true;
      this.searchWithAllDevice12Nc(
        this.deviceData?.device12nc,
        () => {
          this.appState.setCompareFirmwareNoLatest(this.deviceData["deviceSoftwareVersion"]);
          if (this.searchName && this.searchName.length > 0) {
            this.deviceInfo = this.searchName[0];
            this.getConfigurationNumber();
          } else {
            // Device 12NC from the imported DCF is not present in this
            // environment's database (search API returned []). Without this
            // guard the import flow is stuck on the loading spinner.
            this.showDeviceUnavailableError(this.deviceData?.device12nc);
          }
        },
        () => {
          this.showDeviceUnavailableError(this.deviceData?.device12nc);
        }
      );

    }
  }

  private showDeviceUnavailableError(device12nc?: string) {
    this.loading = false;
    this.loadingUpload = false;
    this.showFileName = false;
    this.fileLoad = false;
    this.ismissingfileds = true;
    if (this.dcfImportErrorShown) {
      return;
    }
    this.dcfImportErrorShown = true;
    const deviceName = this.deviceData?.deviceName ?? this.deviceDataFromMetaData?.deviceName ?? "";
    const deviceSwVersion =
      this.deviceData?.deviceSoftwareVersion ?? this.deviceDataFromMetaData?.deviceSoftwareVersion ?? "";
    const resolved12nc = device12nc ?? this.deviceData?.device12nc ?? "";
    this.missingFieldsErrorMsg =
      "This device is currently not supported \n \n" +
      deviceName +
      " " +
      deviceSwVersion +
      "\n 12nc: " +
      resolved12nc +
      "\n \n Please contact your local Signify representative!";
    const modalRef = this.modalService.open(ErrorComponent, {
      keyboard: false,
      centered: true,
      windowClass: ERROR_MODAL_WINDOW_CLASS,
      backdrop: MODAL_BACKDROP_STATIC,
    });
    modalRef.componentInstance.errorName = "Not supported";
    modalRef.componentInstance.errorDescription =
      "Device not supported! Please change the device and try again.";
  }

  onSearchDevice(event) {
    this.enableButton = false;
    if (event === "12nc") {
      this.is12nc = true;
      this.isName = false;
    } else if (event === "productname") {
      this.isName = true;
      this.is12nc = false;
    } else {
      this.isName = false;
      this.is12nc = false;
    }
  }

  selectDeviceName(deivceName) {
    if (deivceName) {
      this.deviceInfo = deivceName;
      this.getSoftwareVersion();

      this.schemalist();

      setTimeout(() => {
        this.getConfigurationNumber();
      }, 1000);

      this.appState.setDevice12nc(deivceName.device12nc);
      this.enableButton = true;
    } else {
      this.enableButton = false;
    }
  }

  select12nc(deivce12) {
    if (deivce12) {
      this.deviceInfo = deivce12;
      this.getSoftwareVersion();

      this.schemalist();
      setTimeout(() => {
        this.getConfigurationNumber();
      }, 1000);

      this.appState.setDevice12nc(deivce12.device12nc);
      this.enableButton = true;
    } else {
      this.enableButton = false;
    }
  }

  onChangeSearch(search: string) {
    this.isError = false;
  }

  configureDevice() {
    // let modes = JSON.parse(localStorage.getItem('modes'));
    // if (modes === "Standard"){

    this.schemalist(() => {
      this.onSubmit();
    });
  }

  remove() {
    this.uiSignals.removeModes("invalid");
  }

  onClearSelection()
  {
    this.filteredDeviceList=this.newDeviceList;
    this.selectedDevice = null;
  }

  selectNewDevice(newdevice) {
    this.deviceSelected = true;
    this.selectedDevice=newdevice;
    const pushCompatibleFeature = [];
    // this.feature12NcName = [];
    let count = 0;
    this.appState.setCompatibleFeatureID(null);
    const previousFeatures = this.appState.getConfigurationData() || {};
    const deviceApp = newdevice["name"].match(/^(\S+)\s(.*)/).slice(1);
    this.searchWithAllDevice12Nc(deviceApp[0]);
    let device = newdevice["name"];
    device = device.split(" ")[0];
    this.configurationData = device;
    setTimeout(() => {
      if (Array.isArray(this.searchName)) {
        this.deviceInfo = this.searchName[0];
      } else {
        this.deviceInfo = this.searchName;
      }

      this.getSoftwareVersion();

      setTimeout(() => {
        this.schemalist();
      }, 2500);
    }, 1500);
    // setTimeout(() => {

    // }, 4000);

    //MOWEB 1.0.1 Changes Only DONE
    setTimeout(() => {
      const compatibleFeatures = [];
      this.feature12NcName.forEach((element) => {
        if (
          !(
            (element === "owp-" || element === "codedmains-")
            //|| element === "touchdim-"
          )
        ) {
          //
          let index =
            element.charAt(0).toUpperCase() + element.substr(1).toLowerCase();
          previousFeatures["feature12Nc"].forEach((item) => {
            item = item.charAt(0).toUpperCase() + item.slice(1);
            if (index === "Luminfoformat3-" || index === "Luminfoformat0-") {
              index = index.slice(0, -3);
              item = item.slice(0, -3);
            }
            if (item === index) {
              index = index.slice(0, -1);
              if (index === "SRPSU" || index === "Srpsu") {
                index = "SR PSU";
              }
              if (index === "Luminfoform") {
                index = "Luminaire info";
              }
              if (index === "Ast") {
                index = "AST";
              }
              if (index === "Dalipsu") {
                index = "DALI PSU";
              }
              if (index === "Mtp") {
                index = "MTP";
              }
              if (index === "Ztv") {
                index = "0-10V / 1-10V";
              }
              if (index === "touchdim") {
                index = "Touch and Dim";
              }
              compatibleFeatures.push(index);
              count++;
            }
          });
        }
      });
      this.feature12NcName.forEach((element) => {
        previousFeatures["feature12Nc"].forEach((item) => {
          if (element === "luminfoformat3-" || element === "luminfoformat0-") {
            if (element === item) {
              compatibleFeatures.push("Luminaire info");
              count++;
            }
          }
        });
      });
      compatibleFeatures.forEach((item) => {
        const comparableItem = item === "Luminaire info" ? "LumInfo" : item;

        previousFeatures["selectedFeaturesInfo"].forEach((element) => {
          if (
            comparableItem === element ||
            matchesFeatureSelectionAlias(comparableItem, element)
          ) {
            pushCompatibleFeature.push(element);
          }
        });
      });
      const ver = this.appState.getCompareFirmwareNoLatest() || {};
      this.searchName[0].devicetypePicture =
        "https://www.api.easydesignintool.signify.com" +
        this.searchName[0].devicetypePicture;

      //Release 0.14 Chnages Only MOWEB 1.0.1 DONE
      for (let index = 0; index < compatibleFeatures.length; index++) {
        const element = compatibleFeatures[index];
        if (
          element === "owp-" ||
          element === "codedmains-" ||
          element === "corridor-" ||
          element === "touchdim-" ||
          element === "mtp-" ||
          element === "ampdim-" ||
          element === "dcemergency-" ||
          element === "mindimlevel" ||
          element === "dtl-"
        ) {
          compatibleFeatures.splice(index, 1, "zzRemovedFeature");
        }
      }
      compatibleFeatures.sort((a, b) => a.localeCompare(b));
      compatibleFeatures.forEach((element) => {
        if (element === "zzRemovedFeature") {
          compatibleFeatures.splice(
            compatibleFeatures.indexOf("zzRemovedFeature")
          );
        }
      });
      this.appState.setCompatibleFeatureID(compatibleFeatures);
      this.deviceInfo = {
        configurationID: previousFeatures.configurationID,
        deviceName: this.deviceInfo.deviceName,
        device12nc: this.deviceInfo.device12nc,
        softwareVerSchema: this.softwareVersionSchema,
        picture: this.searchName[0].devicetypePicture,
        luminSoftVer0: this.lumin0SoftwareVersionSchema,
        luminSoftVer3: this.lumin3SoftwareVersionSchema,
        luminSoftVer4: this.lumin4SoftwareVersionSchema,
        luminSoftVer5: this.lumin5SoftwareVersionSchema,
        dtlSoftVer: this.dtlSoftwareVersionSchema,
        flextuneSoftVer: this.flextuneSoftwareVersionSchema,
        mindimlevelSoftVer: this.mindimlevelSoftwareVersionSchema,
        dcemergencySoftVer: this.dcemergencySoftwareVersionSchema,
        ampdimSoftVer: this.ampdimSoftwareVersionSchema,
        lineswitchSoftVer: this.lineswitchSoftwareVersionSchema,
        owpSoftVer: this.owpSoftwareVersionSchema,
        codedmainsSoftVer: this.codedmainsSoftwareVersionSchema,
        dalipsuSoftVer: this.dalipsuSoftwareVersionSchema,
        dali102AddressingSoftVer: this.daliaddressing102SoftwareVersionSchema,
        dali102and207ConfigSoftVer: this.dali102and207ConfigSoftwareVersionSchema,
        auxSoftVer: this.auxSoftwareVersionSchema,
        lsiSoftVer: this.lsiSoftwareVersionSchema,
        aocmultichannelSoftVer: this.aocmultichannelSoftwareVersionSchema,
        otpSoftVer: this.otpSoftwareVersionSchema,
        dscSoftVer: this.dscSoftwareVersionSchema,
        lowmainslockSoftVer: this.lowmainslockSoftwareVersionSchema,
        dipswitchSoftVer: this.dipswitchSoftwareVersionSchema,
        mtpSoftVer: this.mtpSoftwareVersionSchema,
        dimminginterfaceSoftVer: this.dimminginterfaceSoftwareVersionSchema,
        dynadimmerSoftVer: this.dynadimmerSoftwareVersionSchema,
        ztvSoftVer: this.ztvSoftwareVersionSchema,
        ztvoutputSoftVer: this.ztvoutputSoftwareVersionSchema,
        lipSoftVer: this.lipSoftwareVersionSchema,
        rsoSoftVer: this.rsoSoftwareVersionSchema,
        aocSoftVer: this.aocSoftwareVersionSchema,
        touchdimSoftVer: this.touchdimSoftwareVersionSchema,
        diia253maintenanceSoftVer: this.diia253maintenanceSoftwareVersionSchema,
        corridorSoftVer: this.corridorSoftwareVersionSchema,
        astSoftVer: this.astSoftwareVersionSchema,
        cloliteSoftVer: this.cloliteSoftwareVersionSchema,
        cloSoftVer: this.cloSoftwareVersionSchema,
        multiconfigSoftVer: this.multiConfigSoftwareSchema,
        selectedFeaturesInfo: this.feature12NcName,
        feature12Nc: this.feature12NcName,
        resp: previousFeatures.resp,
        softwareVersion: ver,
      };
      if (count === this.feature12NcName.length) {
        this.confirm();
      }
      this.deviceSelected = false;
    }, 6000);
  }

  confirm() {
    this.service.setConfigurationData(this.deviceInfo);
    this.activemodal.close();
    this.configureDevice();
    this.uiSignals.callConfigMethod("");
    // localStorage.removeItem('configurationData');
  }

  setconfiguration() {
    // Edit Device functionality removed - this method is now a no-op
  }

  onChangeVersion(selectedFirmware) {
    this.appState.setCompareFirmwareNoLatest(selectedFirmware);
  }

  ngDoCheck() {
    if (this.appState.getCloseConfigureModal()) {
      this.activemodal.close();
      this.appState.removeCloseConfigureModal();
    }
  }

  loadFFStandardMode() {
    const modes = this.appState.getStoreConfigMode();
    if (
      modes["storeConfigModeData"] === "Standard" &&
      modes["storeConfigModeCheck"]
    ) {
      this.startConfigurationData["selectedFeaturesInfo"] = this.featureArray;
      this.startConfigurationData["feature12Nc"] = [
        ...new Set(this.feature12NcName),
      ];

      // Prioritize existing configured data to prevent data loss on refresh

      // Merge existing resp object from ConfigurationData or SaveSetData
      const existingConfigData = this.appState.getConfigurationData();
      const existingSaveSetData = this.appState.getSaveSetData();
      if (existingConfigData && existingConfigData["resp"]) {
        // Use existing configured values directly to preserve all user configurations
        this.startConfigurationData["resp"] = existingConfigData["resp"];
      } else if (existingSaveSetData && existingSaveSetData["resp"]) {
        // Use existing saved values directly to preserve all user configurations
        this.startConfigurationData["resp"] = existingSaveSetData["resp"];
      } else if (!this.startConfigurationData["resp"]) {
        // Initialize empty resp object to ensure it exists for feature configurations
        this.startConfigurationData["resp"] = {};
      }

      this.aloAoc.sendQuickConfig("quickConfig");
      this.appState.setQuickConfiguration("quickConfig");
      this.service.setConfigurationData(this.startConfigurationData);
      // Create deep copy for SaveSetData
      const saveDataCopy = JSON.parse(JSON.stringify(this.startConfigurationData));
      this.appState.setSaveSetData(saveDataCopy);
      this.appState.setSaveSetData1(saveDataCopy);
      // Force immediate save to sessionStorage before navigation
      this.appState.forcePersistState();
      this.route.navigate(["/featureLanding"]);
    }
  }

  // ngAfterViewInit(){
  //   //this.onItemChange('existing');
  // }
  ZTV = false;
  ALO = false;
  AOC = false;
  CLO = false;
  DCEmergency = false;
  DTL = false;
  PSU = false;
  LuminInfo = false;
  touchDim = false;
  corridor = false;
  startuptime = false;
  minDimLevel = false;
  selectAll($event: MouseEvent) {

  }

  unselectAll($event: MouseEvent) {

  }

  onFeatureChange(ztv: string, checked: boolean) {

  }

  exportFile(checked: boolean) {

  }

  onFileDropped(files: FileList) {
    if (files.length > 1) {
      this.showDragArea = false;
      this.ismissingfileds = true;
      this.ismissingfileds = true;
      const modalRef = this.modalService.open(ErrorComponent, {
        keyboard: false,
        centered: true,
        windowClass: ERROR_MODAL_WINDOW_CLASS,
        backdrop: MODAL_BACKDROP_STATIC,
      });
      modalRef.componentInstance.errorName = "File selection error";
      modalRef.componentInstance.errorDescription = "Only one file is allowed. Please select a single file.";
      return;
    }
    const file = files[0];
    if (!file || (file.type !== "application/json" && !file.name.endsWith(".json"))) {
      this.showDragArea = false;
      this.showDragArea = false;
      this.ismissingfileds = true;
      const modalRef = this.modalService.open(ErrorComponent, {
        keyboard: false,
        centered: true,
        windowClass: ERROR_MODAL_WINDOW_CLASS,
        backdrop: MODAL_BACKDROP_STATIC,
      });
      modalRef.componentInstance.errorName = "File type error";
      modalRef.componentInstance.errorDescription = "Wrong file type. Please select a JSON file.";
      return;
    }
    this.fileBrowseHandler(files);
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer && event.dataTransfer.files.length > 1) {

      this.showDragArea = false;
      this.ismissingfileds = true;
      this.missingFieldsErrorMsg = "Only one file is allowed! Please select a single file";
      const modalRef = this.modalService.open(ErrorComponent, {
        keyboard: false,
        centered: true,
        windowClass: ERROR_MODAL_WINDOW_CLASS,
        backdrop: MODAL_BACKDROP_STATIC,
      });
      modalRef.componentInstance.errorName = "File selection error";
      modalRef.componentInstance.errorDescription = this.missingFieldsErrorMsg;
      return;
    }
    if (event.dataTransfer && event.dataTransfer.files.length === 1) {
      const file = event.dataTransfer.files[0];
      if (!file || (file.type !== "application/json" && !file.name.endsWith(".json"))) {
        this.showDragArea = false;
        this.ismissingfileds = true;
        this.missingFieldsErrorMsg = "Wrong file type! Please select a JSON file";
        const modalRef = this.modalService.open(ErrorComponent, {
          keyboard: false,
          centered: true,
          windowClass: ERROR_MODAL_WINDOW_CLASS,
          backdrop: MODAL_BACKDROP_STATIC,
        });
        modalRef.componentInstance.errorName = "File type error";
        modalRef.componentInstance.errorDescription = this.missingFieldsErrorMsg;
        return;
      }
      this.fileBrowseHandler(event.dataTransfer.files);
    }
  }


  onDragOver(event: DragEvent) {
    event.preventDefault();
    // Optionally add visual feedback
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    // Optionally remove visual feedback
  }

  preventDefaultWindow = (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

}
